import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../hooks/useAuth';
import { ConfirmationModal, MessageModal, UnsavedChangesModal, PasswordChangeModal } from '../components/modals';
import { extractErrorMessage, formatDate, getFileIcon, isImageFile, isPdfFile } from '../utils/helpers';
import DocumentCard from '../components/shared/DocumentCard';
import DocumentViewerModal from '../components/shared/DocumentViewerModal';
import PlanViewer from '../components/shared/PlanViewer';
import RGPDConsentNotification from '../components/shared/RGPDConsentNotification';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ClientDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [activeTab, setActiveTab] = useState('account'); // 'account', 'cemetery', 'contact', or 'rgpd'
  
  // New concessions state
  const [concessions, setConcessions] = useState([]);
  const [expandedConcessions, setExpandedConcessions] = useState(new Set());
  const [editingConcession, setEditingConcession] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [showConcessionModal, setShowConcessionModal] = useState(false);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [currentConcessionForPerson, setCurrentConcessionForPerson] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState({
    email: '',
    city_hall_address: '',
    mayor_name: '',
    secretary_name: '',
    phone_number: ''
  });
  const [emailMessage, setEmailMessage] = useState({ type: '', text: '' }); // type: 'error', 'success', 'info'
  
  // Form data tracking for unsaved changes detection
  const [concessionFormData, setConcessionFormData] = useState({
    num_plan: '',
    acheteur: '',
    num_concession: '',
    date_achat: '',
    duree_concession: '',
    nombre_places: 1,
    notes: ''
  });
  const [personFormData, setPersonFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    date_deces: '',
    notes: ''
  });
  const [hasUnsavedConcessionChanges, setHasUnsavedConcessionChanges] = useState(false);
  const [hasUnsavedPersonChanges, setHasUnsavedPersonChanges] = useState(false);
  
  // Unsaved changes confirmation modal
  const [unsavedChangesModal, setUnsavedChangesModal] = useState({
    isOpen: false,
    type: '', // 'concession' or 'person'
    onSave: () => {},
    onDiscard: () => {}
  });
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('num_plan'); // Default sort by 'num_plan'
  const [sortDirection, setSortDirection] = useState('asc'); // Default ascending
  
  // Search state with autocomplete
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    requestType: 'Question',
    email: '',
    subject: '',
    message: '',
    attachment: null
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactMessage, setContactMessage] = useState({ type: '', text: '' });
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });
  
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  // File management state
  const [concessionFiles, setConcessionFiles] = useState([]);
  const [personFiles, setPersonFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lightboxFile, setLightboxFile] = useState(null);
  // Temporary files for new concession (before creation)
  const [tempConcessionFiles, setTempConcessionFiles] = useState([]);
  // Temporary files for new person (before creation)
  const [tempPersonFiles, setTempPersonFiles] = useState([]);
  
  // Cemetery plan state
  const [cemeteryPlan, setCemeteryPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  
  // PDF generation success modal
  const [showPdfSuccessModal, setShowPdfSuccessModal] = useState(false);
  const [pdfModalInfo, setPdfModalInfo] = useState({ persons: 0, pages: 0 });
  
  // RGPD states
  const [showRGPDModal, setShowRGPDModal] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [rgpdExportLoading, setRgpdExportLoading] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [showFirstLoginConsentModal, setShowFirstLoginConsentModal] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  
  const { user, logout } = useAuth();
  
  // Check if user is in demo mode (read-only)
  const isDemoMode = user?.role === 'demo';
  
  // Desktop Shortcut Download Function
  const downloadShortcut = () => {
    const appUrl = window.location.origin;
    const appName = 'CIMEO';
    
    // Detect OS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMac = /Mac|iPhone|iPad|iPod/.test(userAgent);
    const isWindows = /Win/.test(userAgent);
    
    let content, filename, mimeType;
    
    if (isMac) {
      // Create .webloc file for Mac
      content = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>URL</key>
    <string>${appUrl}</string>
</dict>
</plist>`;
      filename = `${appName}.webloc`;
      mimeType = 'application/xml';
    } else {
      // Create .url file for Windows and others
      content = `[InternetShortcut]
URL=${appUrl}
IconIndex=0`;
      filename = `${appName}.url`;
      mimeType = 'text/plain';
    }
    
    // Create blob and download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`Desktop shortcut downloaded: ${filename}`);
  };

  // Check if required fields are filled
  const hasRequiredFields = () => {
    return profile && 
           profile.city_hall_address && 
           profile.mayor_name && 
           profile.secretary_name &&
           profile.phone_number &&
           profile.city_hall_address.trim() !== '' &&
           profile.mayor_name.trim() !== '' &&
           profile.secretary_name.trim() !== '' &&
           profile.phone_number.trim() !== '';
  };

  // Check if cemetery tab should be accessible
  const canAccessCemetery = () => {
    // Must have required fields, active subscription, AND RGPD consent
    return hasRequiredFields() && 
           profile?.subscription_active === true && 
           !profile?.subscription_expired &&
           profile?.rgpd_consent === true;
  };
  
  // Check if user has RGPD consent (for blocking features)
  const hasRGPDConsent = () => {
    return profile?.rgpd_consent === true;
  };

  useEffect(() => {
    fetchProfile();
    fetchConcessions();
  }, []);

  useEffect(() => {
    if (activeTab === 'plan') {
      fetchCemeteryPlan();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/client/profile`);
      setProfile(response.data);
      setEditedProfile({
        email: response.data.email || '',
        city_hall_address: response.data.city_hall_address || '',
        mayor_name: response.data.mayor_name || '',
        secretary_name: response.data.secretary_name || '',
        phone_number: response.data.phone_number || ''
      });
      
      // Check if user needs to give RGPD consent (first login or no consent)
      if (!response.data.rgpd_consent) {
        setShowFirstLoginConsentModal(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Real-time email validation
  const validateEmailRealtime = (email) => {
    if (!email || email.trim() === '') {
      return { valid: false, message: '' }; // Don't show error for empty while typing
    }
    
    if (!email.includes('@')) {
      return { valid: false, message: 'Veuillez saisir une adresse e-mail valide.' };
    }
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      return { valid: false, message: 'Veuillez saisir une adresse e-mail valide.' };
    }
    
    return { valid: true, message: '' };
  };

  const handleSaveProfile = async () => {
    // Block save in demo mode
    if (isDemoMode) {
      setMessageModal({
        isOpen: true,
        title: 'Mode Démo',
        message: 'Les modifications ne peuvent pas être enregistrées en mode démonstration.',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Check if email has changed
      const emailChanged = editedProfile.email.trim() !== profile.email;
      
      if (emailChanged) {
        // 1. Validate empty field
        if (!editedProfile.email || editedProfile.email.trim() === '') {
          setEmailMessage({ type: 'error', text: 'Veuillez saisir une adresse e-mail valide.' });
          return;
        }
        
        // 2. Validate email format
        const validation = validateEmailRealtime(editedProfile.email);
        if (!validation.valid) {
          setEmailMessage({ type: 'error', text: validation.message });
          return;
        }
        
        // 3. Request email change (requires confirmation)
        // Backend will check for duplicates
        try {
          const emailResponse = await axios.post(`${API}/client/request-email-change`, {
            new_email: editedProfile.email.trim().toLowerCase()
          });
          
          // 4. Display confirmation workflow message
          setEmailMessage({ 
            type: 'info', 
            text: 'Votre nouvelle adresse e-mail doit être confirmée. Veuillez vérifier votre boîte mail.' 
          });
          
          // Reset email field to original value (not updated until confirmed)
          setEditedProfile({
            ...editedProfile,
            email: profile.email
          });
          
          // Message remains visible until user dismisses it
        } catch (emailError) {
          // Handle specific error messages
          if (emailError.response?.status === 400) {
            const errorDetail = emailError.response.data.detail;
            
            // 3. Duplicate email check
            if (errorDetail.includes('déjà utilisée')) {
              setEmailMessage({ 
                type: 'error', 
                text: 'Cette adresse e-mail est déjà utilisée. Veuillez en choisir une autre.' 
              });
            } 
            // 2. Invalid email format (from backend)
            else if (errorDetail.includes('valide')) {
              setEmailMessage({ type: 'error', text: 'Veuillez saisir une adresse e-mail valide.' });
            }
            // General error
            else {
              setEmailMessage({ type: 'error', text: errorDetail });
            }
          } else {
            setEmailMessage({ type: 'error', text: 'Erreur lors de la demande de changement d\'e-mail' });
          }
          return;
        }
      }
      
      // Update other profile fields (not email)
      const profileData = {
        city_hall_address: editedProfile.city_hall_address,
        mayor_name: editedProfile.mayor_name,
        secretary_name: editedProfile.secretary_name,
        phone_number: editedProfile.phone_number
      };
      
      const response = await axios.put(`${API}/client/profile`, profileData);
      
      // Update local state immediately with the response
      if (response.data.profile) {
        setProfile(response.data.profile);
      } else if (response.data) {
        setProfile(response.data);
      }
      
      setIsEditingProfile(false);
      
      if (emailChanged) {
        // 6. Message for new request (backend handles canceling previous)
        setEmailMessage({ 
          type: 'success', 
          text: 'Une nouvelle demande de modification d\'adresse e-mail a été envoyée. Veuillez vérifier votre boîte mail.' 
        });
        // Message remains visible until user dismisses it
      } else {
        setMessageModal({
          isOpen: true,
          title: 'Succès',
          message: 'Informations mises à jour avec succès',
          type: 'success'
        });
      }
      
      // Refresh to ensure all fields are synced with backend
      await fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de la mise à jour'),
        type: 'error'
      });
    }
  };

  const handleCancelEditProfile = () => {
    setEditedProfile({
      email: profile?.email || '',
      city_hall_address: profile?.city_hall_address || '',
      mayor_name: profile?.mayor_name || '',
      phone_number: profile?.phone_number || ''
    });
    setEmailMessage({ type: '', text: '' }); // Clear email messages
    setIsEditingProfile(false);
  };

  const fetchConcessions = async () => {
    try {
      const response = await axios.get(`${API}/client/concessions`);
      setConcessions(response.data);
    } catch (error) {
      console.error('Error fetching concessions:', error);
    }
  };

  const fetchCemeteryPlan = async () => {
    setPlanLoading(true);
    try {
      const response = await axios.get(`${API}/client/cemetery-plan`);
      setCemeteryPlan(response.data);
    } catch (error) {
      console.error('Error fetching cemetery plan:', error);
      setCemeteryPlan(null);
    } finally {
      setPlanLoading(false);
    }
  };

  // Concession handlers
  const handleAddConcession = () => {
    setEditingConcession(null);
    // Initialize empty form data
    setConcessionFormData({
      num_plan: '',
      acheteur: '',
      num_concession: '',
      date_achat: '',
      duree_concession: '',
      nombre_places: 1,
      notes: ''
    });
    setHasUnsavedConcessionChanges(false);
    setShowConcessionModal(true);
    setTempConcessionFiles([]);
    setConcessionFiles([]);
  };

  const handleEditConcession = async (concession) => {
    setEditingConcession(concession);
    // Initialize form data with existing values
    setConcessionFormData({
      num_plan: concession.num_plan || '',
      acheteur: concession.acheteur || '',
      num_concession: concession.num_concession || '',
      date_achat: concession.date_achat || '',
      duree_concession: concession.duree_concession || '',
      nombre_places: concession.nombre_places || 1,
      notes: concession.notes || ''
    });
    setHasUnsavedConcessionChanges(false);
    setShowConcessionModal(true);
    setTempConcessionFiles([]);
    // Load files for this concession
    await fetchConcessionFiles(concession.id);
  };

  // Handle concession form field changes
  const handleConcessionFormChange = (field, value) => {
    setConcessionFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedConcessionChanges(true);
  };

  // Handle close concession modal with unsaved changes check
  const handleCloseConcessionModal = () => {
    if (hasUnsavedConcessionChanges) {
      setUnsavedChangesModal({
        isOpen: true,
        type: 'concession',
        onSave: () => {
          // Trigger form submission
          const form = document.getElementById('concession-form');
          if (form) form.requestSubmit();
          setUnsavedChangesModal({ isOpen: false, type: '', onSave: () => {}, onDiscard: () => {} });
        },
        onDiscard: () => {
          setShowConcessionModal(false);
          setEditingConcession(null);
          setHasUnsavedConcessionChanges(false);
          setUnsavedChangesModal({ isOpen: false, type: '', onSave: () => {}, onDiscard: () => {} });
        }
      });
    } else {
      setShowConcessionModal(false);
      setEditingConcession(null);
    }
  };

  const handleSaveConcession = async (formData) => {
    // Block save in demo mode
    if (isDemoMode) {
      setMessageModal({
        isOpen: true,
        title: 'Mode Démo',
        message: 'Les modifications ne peuvent pas être enregistrées en mode démonstration.',
        type: 'warning'
      });
      return;
    }
    
    try {
      if (editingConcession) {
        // Update
        const response = await axios.put(`${API}/client/concessions/${editingConcession.id}`, formData);
        setConcessions(concessions.map(c => c.id === editingConcession.id ? response.data : c));
      } else {
        // Create
        const response = await axios.post(`${API}/client/concessions`, formData);
        const newConcession = response.data;
        
        // Upload temp files if any
        if (tempConcessionFiles.length > 0) {
          for (const tempFile of tempConcessionFiles) {
            try {
              const fileFormData = new FormData();
              fileFormData.append('file', tempFile.file);
              await axios.post(`${API}/client/concessions/${newConcession.id}/files`, fileFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (fileError) {
              console.error('Error uploading file:', fileError);
            }
          }
        }
        
        setConcessions([...concessions, newConcession]);
        setTempConcessionFiles([]);
      }
      setShowConcessionModal(false);
      setEditingConcession(null);
      setHasUnsavedConcessionChanges(false);
    } catch (error) {
      console.error('Error saving concession:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de l\'enregistrement'),
        type: 'error'
      });
    }
  };

  const handleDeleteConcession = (concessionId) => {
    // Block delete in demo mode
    if (isDemoMode) {
      setMessageModal({
        isOpen: true,
        title: 'Mode Démo',
        message: 'Les suppressions ne sont pas autorisées en mode démonstration.',
        type: 'warning'
      });
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer la concession',
      message: 'Voulez-vous vraiment supprimer cette concession et toutes les personnes associées ? Cette action est irréversible.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/client/concessions/${concessionId}`);
          setConcessions(concessions.filter(c => c.id !== concessionId));
        } catch (error) {
          console.error('Error deleting concession:', error);
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression',
            type: 'error'
          });
        }
      }
    });
  };

  // Person handlers
  const handleAddPerson = (concession) => {
    setCurrentConcessionForPerson(concession);
    setEditingPerson(null);
    // Initialize empty form data
    setPersonFormData({
      nom: '',
      prenom: '',
      date_naissance: '',
      date_deces: '',
      notes: ''
    });
    setHasUnsavedPersonChanges(false);
    setShowPersonModal(true);
    setTempPersonFiles([]);
    setPersonFiles([]);
  };

  const handleEditPerson = async (person, concession) => {
    setCurrentConcessionForPerson(concession);
    setEditingPerson(person);
    // Initialize form data with existing values
    setPersonFormData({
      nom: person.nom || '',
      prenom: person.prenom || '',
      date_naissance: person.date_naissance || '',
      date_deces: person.date_deces || '',
      notes: person.notes || ''
    });
    setHasUnsavedPersonChanges(false);
    setShowPersonModal(true);
    setTempPersonFiles([]);
    // Load files for this person
    await fetchPersonFiles(concession.id, person.id);
  };

  // Handle person form field changes
  const handlePersonFormChange = (field, value) => {
    setPersonFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedPersonChanges(true);
  };

  // Handle close person modal with unsaved changes check
  const handleClosePersonModal = () => {
    if (hasUnsavedPersonChanges) {
      setUnsavedChangesModal({
        isOpen: true,
        type: 'person',
        onSave: () => {
          // Trigger form submission
          const form = document.getElementById('person-form');
          if (form) form.requestSubmit();
          setUnsavedChangesModal({ isOpen: false, type: '', onSave: () => {}, onDiscard: () => {} });
        },
        onDiscard: () => {
          setShowPersonModal(false);
          setEditingPerson(null);
          setCurrentConcessionForPerson(null);
          setHasUnsavedPersonChanges(false);
          setUnsavedChangesModal({ isOpen: false, type: '', onSave: () => {}, onDiscard: () => {} });
        }
      });
    } else {
      setShowPersonModal(false);
      setEditingPerson(null);
      setCurrentConcessionForPerson(null);
    }
  };

  const handleSavePerson = async (formData) => {
    // Block save in demo mode
    if (isDemoMode) {
      setMessageModal({
        isOpen: true,
        title: 'Mode Démo',
        message: 'Les modifications ne peuvent pas être enregistrées en mode démonstration.',
        type: 'warning'
      });
      return;
    }
    
    try {
      if (editingPerson) {
        // Update
        const response = await axios.put(`${API}/client/persons/${editingPerson.id}`, formData);
        setConcessions(concessions.map(c => {
          if (c.id === currentConcessionForPerson.id) {
            return {
              ...c,
              persons: c.persons.map(p => p.id === editingPerson.id ? response.data : p)
            };
          }
          return c;
        }));
      } else {
        // Create
        const response = await axios.post(`${API}/client/concessions/${currentConcessionForPerson.id}/persons`, formData);
        const newPerson = response.data;
        
        // Upload temp files if any
        if (tempPersonFiles.length > 0) {
          for (const tempFile of tempPersonFiles) {
            try {
              const fileFormData = new FormData();
              fileFormData.append('file', tempFile.file);
              await axios.post(`${API}/client/concessions/${currentConcessionForPerson.id}/persons/${newPerson.id}/files`, fileFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (fileError) {
              console.error('Error uploading person file:', fileError);
            }
          }
        }
        
        setConcessions(concessions.map(c => {
          if (c.id === currentConcessionForPerson.id) {
            return {
              ...c,
              persons: [...c.persons, newPerson]
            };
          }
          return c;
        }));
        setTempPersonFiles([]);
      }
      setShowPersonModal(false);
      setEditingPerson(null);
      setCurrentConcessionForPerson(null);
      setHasUnsavedPersonChanges(false);
    } catch (error) {
      console.error('Error saving person:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de l\'enregistrement'),
        type: 'error'
      });
    }
  };

  const handleDeletePerson = (person, concession) => {
    // Block delete in demo mode
    if (isDemoMode) {
      setMessageModal({
        isOpen: true,
        title: 'Mode Démo',
        message: 'Les suppressions ne sont pas autorisées en mode démonstration.',
        type: 'warning'
      });
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer la personne',
      message: `Voulez-vous vraiment supprimer ${person.nom} ${person.prenom || ''} de cette concession ?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/client/persons/${person.id}`);
          setConcessions(concessions.map(c => {
            if (c.id === concession.id) {
              return {
                ...c,
                persons: c.persons.filter(p => p.id !== person.id)
              };
            }
            return c;
          }));
        } catch (error) {
          console.error('Error deleting person:', error);
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression',
            type: 'error'
          });
        }
      }
    });
  };

  // File management handlers
  const fetchConcessionFiles = async (concessionId) => {
    try {
      const response = await axios.get(`${API}/client/concessions/${concessionId}/files`);
      setConcessionFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleFileUpload = async (concessionId, file) => {
    // Validate file type
    const allowedExtensions = ['.txt', '.pdf', '.jpg', '.jpeg', '.png', '.gif'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Format de fichier non autorisé. Formats acceptés : .txt, .pdf, .jpg, .png',
        type: 'error'
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessageModal({
        isOpen: true,
        title: 'Fichier trop volumineux',
        message: 'Fichier trop volumineux (max 10MB)',
        type: 'error'
      });
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/client/concessions/${concessionId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchConcessionFiles(concessionId);
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors du téléchargement du fichier'),
        type: 'error'
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileDelete = async (concessionId, fileId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer le fichier',
      message: 'Voulez-vous vraiment supprimer ce fichier ?',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/client/concessions/${concessionId}/files/${fileId}`);
          await fetchConcessionFiles(concessionId);
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error deleting file:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression du fichier',
            type: 'error'
          });
        }
      }
    });
  };

  const handleFileView = (file, concessionId, personId = null) => {
    setLightboxFile({ ...file, concessionId, personId });
  };

  // Download file function
  const handleFileDownload = async (file, concessionId, personId = null) => {
    try {
      const url = personId 
        ? `${API}/client/concessions/${concessionId}/persons/${personId}/files/${file.id}`
        : `${API}/client/concessions/${concessionId}/files/${file.id}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: 'Erreur lors du téléchargement du fichier',
        type: 'error'
      });
    }
  };

  // Person file handlers
  const fetchPersonFiles = async (concessionId, personId) => {
    try {
      const response = await axios.get(`${API}/client/concessions/${concessionId}/persons/${personId}/files`);
      setPersonFiles(response.data);
    } catch (error) {
      console.error('Error fetching person files:', error);
    }
  };

  const handlePersonFileUpload = async (concessionId, personId, file) => {
    const allowedExtensions = ['.txt', '.pdf', '.jpg', '.jpeg', '.png'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Format de fichier non autorisé. Formats acceptés : .txt, .pdf, .jpg, .png',
        type: 'error'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessageModal({
        isOpen: true,
        title: 'Fichier trop volumineux',
        message: 'Fichier trop volumineux (max 10MB)',
        type: 'error'
      });
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/client/concessions/${concessionId}/persons/${personId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      await fetchPersonFiles(concessionId, personId);
    } catch (error) {
      console.error('Error uploading person file:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors du téléchargement du fichier'),
        type: 'error'
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handlePersonFileDelete = async (concessionId, personId, fileId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer le fichier',
      message: 'Voulez-vous vraiment supprimer ce fichier ?',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/client/concessions/${concessionId}/persons/${personId}/files/${fileId}`);
          await fetchPersonFiles(concessionId, personId);
          setConfirmModal({ ...confirmModal, isOpen: false });
        } catch (error) {
          console.error('Error deleting person file:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setMessageModal({
            isOpen: true,
            title: 'Erreur',
            message: 'Erreur lors de la suppression du fichier',
            type: 'error'
          });
        }
      }
    });
  };

  // Temporary file handlers (for new concession/person before creation)
  const handleTempConcessionFileAdd = (file) => {
    const allowedExtensions = ['.txt', '.pdf', '.jpg', '.jpeg', '.png'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Format de fichier non autorisé. Formats acceptés : .txt, .pdf, .jpg, .png',
        type: 'error'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessageModal({
        isOpen: true,
        title: 'Fichier trop volumineux',
        message: 'Fichier trop volumineux (max 10MB)',
        type: 'error'
      });
      return;
    }

    if (tempConcessionFiles.length >= 4) {
      setMessageModal({
        isOpen: true,
        title: 'Limite atteinte',
        message: 'Maximum 4 fichiers autorisés',
        type: 'warning'
      });
      return;
    }

    // Create temp object with file and preview URL
    const tempFile = {
      id: `temp-${Date.now()}`,
      file: file,
      original_filename: file.name,
      file_type: fileExt,
      file_size: file.size,
      preview: fileExt.match(/\.(jpg|jpeg|png)$/i) ? URL.createObjectURL(file) : null
    };

    setTempConcessionFiles([...tempConcessionFiles, tempFile]);
  };

  const handleTempConcessionFileRemove = (tempId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTempConcessionFiles(tempConcessionFiles.filter(f => f.id !== tempId));
  };

  const handleTempPersonFileAdd = (file) => {
    const allowedExtensions = ['.txt', '.pdf', '.jpg', '.jpeg', '.png'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Format de fichier non autorisé. Formats acceptés : .txt, .pdf, .jpg, .png',
        type: 'error'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessageModal({
        isOpen: true,
        title: 'Fichier trop volumineux',
        message: 'Fichier trop volumineux (max 10MB)',
        type: 'error'
      });
      return;
    }

    if (tempPersonFiles.length >= 4) {
      setMessageModal({
        isOpen: true,
        title: 'Limite atteinte',
        message: 'Maximum 4 fichiers autorisés',
        type: 'warning'
      });
      return;
    }

    const tempFile = {
      id: `temp-${Date.now()}`,
      file: file,
      original_filename: file.name,
      file_type: fileExt,
      file_size: file.size,
      preview: fileExt.match(/\.(jpg|jpeg|png)$/i) ? URL.createObjectURL(file) : null
    };

    setTempPersonFiles([...tempPersonFiles, tempFile]);
  };

  const handleTempPersonFileRemove = (tempId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTempPersonFiles(tempPersonFiles.filter(f => f.id !== tempId));
  };

  // Excel import handler
  const handleExcelImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.xlsm')) {
      setMessageModal({
        isOpen: true,
        title: 'Format non autorisé',
        message: 'Le fichier doit être au format Excel (.xlsx, .xls ou .xlsm)',
        type: 'error'
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/client/concessions/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessageModal({
        isOpen: true,
        title: 'Importation réussie',
        message: `${response.data.message}\n\n📊 ${response.data.imported_count} concession(s) créée(s)\n🔄 ${response.data.updated_count} concession(s) mise(s) à jour\n\nTotal: ${response.data.total_concessions} concession(s)`,
        type: 'success'
      });

      await fetchConcessions();
      event.target.value = '';
      
    } catch (error) {
      console.error('Error importing Excel:', error);
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: extractErrorMessage(error, 'Erreur lors de l\'importation du fichier Excel'),
        type: 'error'
      });
      event.target.value = '';
    }
  };

  // Toggle concession expansion
  const toggleConcession = (concessionId) => {
    const newExpanded = new Set(expandedConcessions);
    if (newExpanded.has(concessionId)) {
      newExpanded.delete(concessionId);
    } else {
      newExpanded.add(concessionId);
    }
    setExpandedConcessions(newExpanded);
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter concessions based on search query
  // Enhanced search with combined first name + last name support
  const matchesSearch = (person, queryWords) => {
    if (!person || queryWords.length === 0) return false;
    
    const nom = (person.nom || '').toLowerCase();
    const prenom = (person.prenom || '').toLowerCase();
    const fullName = `${prenom} ${nom}`.toLowerCase();
    
    // Check if all query words match in any combination
    return queryWords.every(word => 
      fullName.includes(word) || nom.includes(word) || prenom.includes(word)
    );
  };

  const getFilteredConcessions = () => {
    if (!searchQuery.trim()) {
      return concessions;
    }
    
    const query = searchQuery.toLowerCase().trim();
    const queryWords = query.split(/\s+/).filter(w => w.length > 0);
    
    return concessions.filter(concession => {
      // Search across concession fields
      const numPlan = (concession.num_plan || '').toLowerCase();
      const acheteur = (concession.acheteur || '').toLowerCase();
      const numConcession = (concession.num_concession || '').toLowerCase();
      const duree = (concession.duree_concession || '').toString().toLowerCase();
      const dateAchat = concession.date_achat ? new Date(concession.date_achat).toLocaleDateString('fr-FR') : '';
      const dateFin = calculateEndDate(concession.date_achat, concession.duree_concession) || '';
      const jours = calculateRemainingDays(concession.date_achat, concession.duree_concession);
      const joursStr = jours !== null ? (jours === '∞' ? '∞' : jours.toString()) : '';
      
      // Check concession fields
      if (numPlan.includes(query) || acheteur.includes(query) || numConcession.includes(query) || 
          duree.includes(query) || dateAchat.includes(query) || dateFin.includes(query) || joursStr.includes(query)) {
        return true;
      }
      
      // Search across persons with combined name support
      return concession.persons.some(person => matchesSearch(person, queryWords));
    });
  };

  // Generate search suggestions
  const generateSuggestions = (query) => {
    if (!query || query.trim().length === 0) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const queryLower = query.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0);
    const suggestions = [];
    const seenNames = new Set();
    
    // Collect all unique persons from all concessions
    concessions.forEach(concession => {
      if (concession.persons) {
        concession.persons.forEach(person => {
          if (matchesSearch(person, queryWords)) {
            const fullName = `${person.prenom || ''} ${person.nom || ''}`.trim();
            const key = fullName.toLowerCase();
            
            if (!seenNames.has(key) && fullName) {
              seenNames.add(key);
              suggestions.push({
                name: fullName,
                person: person,
                concessionId: concession.id,
                concessionInfo: `${concession.num_plan || ''} - ${concession.acheteur || ''}`
              });
            }
          }
        });
      }
      
      // Also match concession buyer name
      if (concession.acheteur && concession.acheteur.toLowerCase().includes(queryLower)) {
        const key = `concession_${concession.id}`;
        if (!seenNames.has(key)) {
          seenNames.add(key);
          suggestions.push({
            name: concession.acheteur,
            concessionId: concession.id,
            concessionInfo: `${concession.num_plan || ''} - Acheteur`,
            isConcession: true
          });
        }
      }
    });
    
    // Sort by relevance
    suggestions.sort((a, b) => {
      const aLower = a.name.toLowerCase();
      const bLower = b.name.toLowerCase();
      const aExact = aLower === queryLower;
      const bExact = bLower === queryLower;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      return aLower.localeCompare(bLower);
    });
    
    setSearchSuggestions(suggestions.slice(0, 8));
    setShowSuggestions(suggestions.length > 0);
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    generateSuggestions(value);
  };
  
  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
    // Optionally expand the concession row
    if (suggestion.concessionId) {
      const newExpanded = new Set(expandedConcessions);
      newExpanded.add(suggestion.concessionId);
      setExpandedConcessions(newExpanded);
    }
  };

  // Sort concessions
  const getSortedConcessions = () => {
    const filtered = getFilteredConcessions();
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'num_plan':
          aVal = (a.num_plan || '').toLowerCase();
          bVal = (b.num_plan || '').toLowerCase();
          break;
        case 'acheteur':
          aVal = (a.acheteur || '').toLowerCase();
          bVal = (b.acheteur || '').toLowerCase();
          break;
        case 'num_concession':
          aVal = (a.num_concession || '').toLowerCase();
          bVal = (b.num_concession || '').toLowerCase();
          break;
        case 'date_achat':
          aVal = a.date_achat ? new Date(a.date_achat).getTime() : 0;
          bVal = b.date_achat ? new Date(b.date_achat).getTime() : 0;
          break;
        case 'duree_concession':
          aVal = a.duree_concession === 'perpétuelle' ? Infinity : (parseInt(a.duree_concession) || 0);
          bVal = b.duree_concession === 'perpétuelle' ? Infinity : (parseInt(b.duree_concession) || 0);
          break;
        case 'jours_restants':
          const daysA = calculateRemainingDays(a.date_achat, a.duree_concession);
          const daysB = calculateRemainingDays(b.date_achat, b.duree_concession);
          aVal = daysA === '∞' ? Infinity : (typeof daysA === 'number' ? daysA : 0);
          bVal = daysB === '∞' ? Infinity : (typeof daysB === 'number' ? daysB : 0);
          break;
        case 'date_fin':
          const endDateA = calculateEndDate(a.date_achat, a.duree_concession);
          const endDateB = calculateEndDate(b.date_achat, b.duree_concession);
          aVal = endDateA && endDateA !== '-' ? new Date(endDateA.split('/').reverse().join('-')).getTime() : 0;
          bVal = endDateB && endDateB !== '-' ? new Date(endDateB.split('/').reverse().join('-')).getTime() : 0;
          break;
        case 'nombre_places':
          aVal = a.nombre_places || 0;
          bVal = b.nombre_places || 0;
          break;
        default:
          return 0;
      }
      
      // Compare values
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  // Handle print list - Generate PDF with 3 columns and 61 rows
  const handlePrintList = async () => {
    // Collect all persons from all concessions
    const allPersons = [];
    
    concessions.forEach(concession => {
      if (concession.persons && concession.persons.length > 0) {
        concession.persons.forEach(person => {
          allPersons.push({
            nom: person.nom || '',
            prenom: person.prenom || '',
            numPlan: concession.num_plan || ''
          });
        });
      }
    });
    
    // Sort alphabetically by last name (nom)
    allPersons.sort((a, b) => {
      const nameA = a.nom.toLowerCase();
      const nameB = b.nom.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    
    console.log('Total persons to print:', allPersons.length);
    
    // Check if there are persons to print
    if (allPersons.length === 0) {
      setMessageModal({
        isOpen: true,
        title: 'Aucune personne',
        message: 'Aucune personne à imprimer. Veuillez d\'abord ajouter des personnes aux concessions.',
        type: 'info'
      });
      return;
    }
    
    // Load logo as base64
    let logoBase64 = null;
    try {
      const logoResponse = await fetch('/cimeo-logo.png');
      const logoBlob = await logoResponse.blob();
      const reader = new FileReader();
      logoBase64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(logoBlob);
      });
    } catch (e) {
      console.warn('Could not load logo:', e);
    }
    
    // Generate PDF
    generatePDF(allPersons, logoBase64);
  };
  
  // Generate PDF with 3 columns - INDEPENDENT COLUMN HEIGHTS
  const generatePDF = (persons, logoBase64) => {
    // Create PDF document (A4 portrait)
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Page margins and layout settings
    const logoHeight = 10;
    const marginTop = 10 + logoHeight;
    const marginBottom = 10;
    const marginLeft = 8;
    const marginRight = 8;
    const columnGap = 3;
    
    const availableWidth = pageWidth - marginLeft - marginRight;
    const columnWidth = (availableWidth - (2 * columnGap)) / 3;
    
    // Cell dimensions
    const headerHeight = 6;
    const fixedEmptyRowHeight = 5; // Fixed height for EMPTY cells
    const lineHeight = 3.5; // PROPER line spacing - not compressed
    const fontSize = 8;
    const padding = 1;
    const cellPaddingTop = 1.2; // Padding at top of cell
    const cellPaddingBottom = 1.2; // Padding at bottom of cell
    
    // Cell widths within each column
    const nomWidth = columnWidth * 0.4;
    const prenomWidth = columnWidth * 0.4;
    const planWidth = columnWidth * 0.2;
    
    // Helper function to calculate required lines for text
    const calculateLines = (text, maxWidth) => {
      if (!text) return [];
      doc.setFontSize(fontSize);
      return doc.splitTextToSize(String(text), maxWidth - 2 * padding);
    };
    
    // Helper function to draw logo
    const drawLogo = () => {
      const logoX = marginLeft;
      const logoY = 10;
      const logoWidthDraw = 20;
      const logoHeightDraw = 8;
      
      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidthDraw, logoHeightDraw);
        } catch (e) {
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 194, 255);
          doc.text('CIMÉO', logoX, logoY + 8);
        }
      } else {
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 194, 255);
        doc.text('CIMÉO', logoX, logoY + 8);
      }
    };
    
    // Helper function to draw column header
    const drawColumnHeader = (xStart, yPos) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.1);
      doc.setFillColor(220, 220, 220);
      
      doc.rect(xStart, yPos, nomWidth, headerHeight, 'FD');
      doc.rect(xStart + nomWidth, yPos, prenomWidth, headerHeight, 'FD');
      doc.rect(xStart + nomWidth + prenomWidth, yPos, planWidth, headerHeight, 'FD');
      
      doc.setFontSize(fontSize);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Nom', xStart + nomWidth / 2, yPos + 4, { align: 'center' });
      doc.text('Prénom', xStart + nomWidth + prenomWidth / 2, yPos + 4, { align: 'center' });
      doc.text('N° Plan', xStart + nomWidth + prenomWidth + planWidth / 2, yPos + 4, { align: 'center' });
    };
    
    // Calculate dynamic cell height based on content - with PROPER spacing
    const calculateCellHeight = (person) => {
      if (!person) return fixedEmptyRowHeight;
      
      const nom = person.nom || '';
      const prenom = person.prenom || '';
      const numPlan = person.numPlan || '';
      
      const nomLines = calculateLines(nom, nomWidth);
      const prenomLines = calculateLines(prenom, prenomWidth);
      const planLines = calculateLines(numPlan, planWidth);
      
      const maxLines = Math.max(nomLines.length, prenomLines.length, planLines.length, 1);
      
      // Calculate height: padding + lines with proper spacing + padding
      const calculatedHeight = cellPaddingTop + (maxLines * lineHeight) + cellPaddingBottom;
      
      // Return at least fixedEmptyRowHeight
      return Math.max(fixedEmptyRowHeight, calculatedHeight);
    };
    
    // Helper function to draw a cell with dynamic height
    const drawCell = (person, xStart, yPos, cellHeight) => {
      const nom = person?.nom || '';
      const prenom = person?.prenom || '';
      const numPlan = person?.numPlan || '';
      
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.1);
      
      // Draw cell borders with specific height
      doc.rect(xStart, yPos, nomWidth, cellHeight, 'S');
      doc.rect(xStart + nomWidth, yPos, prenomWidth, cellHeight, 'S');
      doc.rect(xStart + nomWidth + prenomWidth, yPos, planWidth, cellHeight, 'S');
      
      if (nom || prenom || numPlan) {
        doc.setFontSize(fontSize);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        
        // Get text lines
        const nomLines = calculateLines(nom, nomWidth);
        const prenomLines = calculateLines(prenom, prenomWidth);
        const planLines = calculateLines(numPlan, planWidth);
        
        // Calculate vertical centering for each field
        const nomTotalHeight = nomLines.length * lineHeight;
        const prenomTotalHeight = prenomLines.length * lineHeight;
        const planTotalHeight = planLines.length * lineHeight;
        
        // Start Y position - vertically centered in cell
        const nomStartY = yPos + (cellHeight - nomTotalHeight) / 2 + lineHeight * 0.75;
        const prenomStartY = yPos + (cellHeight - prenomTotalHeight) / 2 + lineHeight * 0.75;
        const planStartY = yPos + (cellHeight - planTotalHeight) / 2 + lineHeight * 0.75;
        
        // Draw text lines with PROPER spacing
        for (let i = 0; i < nomLines.length; i++) {
          doc.text(nomLines[i], xStart + padding, nomStartY + (i * lineHeight));
        }
        for (let i = 0; i < prenomLines.length; i++) {
          doc.text(prenomLines[i], xStart + nomWidth + padding, prenomStartY + (i * lineHeight));
        }
        for (let i = 0; i < planLines.length; i++) {
          doc.text(planLines[i], xStart + nomWidth + prenomWidth + planWidth / 2, planStartY + (i * lineHeight), { align: 'center' });
        }
      }
    };
    
    // COLUMN-INDEPENDENT layout: each column manages its own Y position
    const totalPersons = persons.length;
    let pageCount = 0;
    let personIndex = 0;
    
    while (personIndex < totalPersons || pageCount === 0) {
      // Start new page
      if (pageCount > 0) {
        doc.addPage();
      }
      
      // Draw logo
      drawLogo();
      
      // Draw headers for all 3 columns
      for (let col = 0; col < 3; col++) {
        const xStart = marginLeft + (col * (columnWidth + columnGap));
        drawColumnHeader(xStart, marginTop);
      }
      
      // Track each column's data independently
      const columnData = [
        { persons: [], endY: marginTop + headerHeight },
        { persons: [], endY: marginTop + headerHeight },
        { persons: [], endY: marginTop + headerHeight }
      ];
      
      // Distribute persons to columns (fill column by column)
      let tempIndex = personIndex;
      for (let col = 0; col < 3 && tempIndex < totalPersons; col++) {
        let yPos = marginTop + headerHeight;
        
        while (tempIndex < totalPersons) {
          const person = persons[tempIndex];
          const cellHeight = calculateCellHeight(person);
          
          // Check if this person fits in current column
          if (yPos + cellHeight > pageHeight - marginBottom) {
            break; // Move to next column
          }
          
          columnData[col].persons.push({ person, height: cellHeight, yPos });
          yPos += cellHeight;
          tempIndex++;
        }
        columnData[col].endY = yPos;
      }
      
      // Draw each column independently
      for (let col = 0; col < 3; col++) {
        const xStart = marginLeft + (col * (columnWidth + columnGap));
        
        // Draw persons for this column (each with its own dynamic height)
        for (const item of columnData[col].persons) {
          drawCell(item.person, xStart, item.yPos, item.height);
          personIndex++;
        }
        
        // Fill remaining space with FIXED HEIGHT empty rows
        let yPos = columnData[col].endY;
        while (yPos + fixedEmptyRowHeight <= pageHeight - marginBottom) {
          drawCell(null, xStart, yPos, fixedEmptyRowHeight);
          yPos += fixedEmptyRowHeight;
        }
      }
      
      pageCount++;
    }
    
    // Open PDF in new window for printing
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
    } else {
      setMessageModal({
        isOpen: true,
        title: 'Information',
        message: 'Veuillez autoriser les pop-ups pour imprimer le PDF.',
        type: 'info'
      });
    }
    
    // Show success modal
    setPdfModalInfo({ persons: totalPersons, pages: pageCount });
    setShowPdfSuccessModal(true);
  };

  // ==================== RGPD FUNCTIONS ====================
  
  // Export all personal data (RGPD right of access)
  const handleRGPDExport = async () => {
    setRgpdExportLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/client/rgpd/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur lors de l\'export');
      
      const data = await response.json();
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mes_donnees_cimeo_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMessageModal({
        isOpen: true,
        title: 'Export réussi',
        message: 'Vos données personnelles ont été exportées avec succès.',
        type: 'success'
      });
    } catch (error) {
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: 'Impossible d\'exporter vos données. Veuillez réessayer.',
        type: 'error'
      });
    } finally {
      setRgpdExportLoading(false);
    }
  };
  
  // Update RGPD consent
  const handleRGPDConsent = async (consent) => {
    setConsentLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/client/rgpd/consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ consent })
      });
      
      if (!response.ok) throw new Error('Erreur');
      
      // Refresh profile to update consent status
      await fetchProfile();
      
      // Close first login modal if it was open
      setShowFirstLoginConsentModal(false);
      
      if (consent) {
        setMessageModal({
          isOpen: true,
          title: 'Consentement enregistré',
          message: 'Votre consentement au traitement des données a été enregistré. Vous pouvez maintenant utiliser l\'application.',
          type: 'success'
        });
      } else {
        // Show warning that access will be blocked
        setMessageModal({
          isOpen: true,
          title: 'Accès restreint',
          message: 'Votre consentement a été retiré. L\'accès aux fonctionnalités est désormais bloqué. Pour réactiver l\'accès, veuillez donner à nouveau votre consentement.',
          type: 'warning'
        });
      }
    } catch (error) {
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: 'Impossible de mettre à jour votre consentement.',
        type: 'error'
      });
    } finally {
      setConsentLoading(false);
    }
  };
  
  // Handle first login consent acceptance
  const handleFirstLoginConsent = async () => {
    await handleRGPDConsent(true);
  };
  
  // Delete own account (RGPD right to be forgotten)
  const handleDeleteOwnAccount = async () => {
    setDeleteAccountLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/client/rgpd/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la suppression');
      }
      
      // Logout and redirect
      logout();
    } catch (error) {
      setMessageModal({
        isOpen: true,
        title: 'Erreur',
        message: error.message || 'Impossible de supprimer votre compte.',
        type: 'error'
      });
      setDeleteAccountLoading(false);
    }
  };
  
  // ==================== END RGPD FUNCTIONS ====================

  // Calculate end date (Date FC ou CP)
  const calculateEndDate = (dateAchat, dureeConcession) => {
    if (!dateAchat || !dureeConcession) return '';
    
    // Check for perpetual
    const duree = dureeConcession.toLowerCase().trim();
    if (duree.includes('perpét') || duree === 'perpetuelle' || duree === 'perpetuel') {
      return 'Concession perpétuelle';
    }

    try {
      const purchaseDate = new Date(dateAchat);
      const years = parseFloat(dureeConcession);
      if (isNaN(years)) return '';

      const endDate = new Date(purchaseDate);
      endDate.setFullYear(endDate.getFullYear() + Math.floor(years));
      // Add remaining months if decimal
      const remainingMonths = Math.round((years % 1) * 12);
      endDate.setMonth(endDate.getMonth() + remainingMonths);

      // Format as YYYY-MM-DD or DD/MM/YYYY
      return endDate.toLocaleDateString('fr-FR');
    } catch (e) {
      return '';
    }
  };

  // Calculate remaining days
  const calculateRemainingDays = (dateAchat, dureeConcession) => {
    if (!dateAchat || !dureeConcession) return null;
    
    // Check for perpetual (case-insensitive)
    const duree = dureeConcession.toLowerCase().trim();
    if (duree.includes('perpét') || duree === 'perpetuelle' || duree === 'perpetuel') {
      return '∞';
    }

    try {
      const purchaseDate = new Date(dateAchat);
      const years = parseFloat(dureeConcession);
      if (isNaN(years)) return null;

      const expiryDate = new Date(purchaseDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + Math.floor(years));
      // Add remaining months if decimal (e.g., 0.25 years = 3 months)
      const remainingMonths = Math.round((years % 1) * 12);
      expiryDate.setMonth(expiryDate.getMonth() + remainingMonths);

      const today = new Date();
      const diffTime = expiryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays;
    } catch (e) {
      return null;
    }
  };

  // Get color for remaining days
  const getRemainingDaysColor = (days) => {
    if (days === '∞') return '#008000'; // Green for perpetual
    if (days === null) return '#000000'; // Black for invalid
    if (days <= 0) return '#FF0000'; // Red for expired
    if (days <= 90) return '#FFA500'; // Orange for expiring soon
    return '#008000'; // Green for valid
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#1A1E2E'}}>
      {/* Header */}
      <div className="shadow-lg" style={{backgroundColor: '#1C2133'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              {/* CIEMO Logo avec effet néon */}
              <img 
                src="/cimeo-logo.png" 
                alt="CIMÉO" 
                style={{
                  height: '42px',
                  width: 'auto',
                  opacity: 0.95,
                  filter: 'drop-shadow(0 4px 12px rgba(0, 194, 255, 0.4)) drop-shadow(0 0 8px rgba(0, 194, 255, 0.3))',
                  transition: 'filter 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 6px 16px rgba(0, 194, 255, 0.6)) drop-shadow(0 0 12px rgba(0, 194, 255, 0.5))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'drop-shadow(0 4px 12px rgba(0, 194, 255, 0.4)) drop-shadow(0 0 8px rgba(0, 194, 255, 0.3))';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold" style={{color: '#FFFFFF'}}>Tableau de bord</h1>
                <p className="text-sm" style={{color: '#8A92B2'}}>{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user?.is_first_login === true && (
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-4 py-2 rounded-md transition-all duration-300"
                  style={{
                    backgroundColor: '#FFC107',
                    color: '#1A1E2E'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 193, 7, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  Changer mot de passe
                </button>
              )}
              
              {/* Desktop Shortcut Button - Downloads shortcut file directly */}
              <button
                onClick={downloadShortcut}
                className="px-4 py-2 rounded-md transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                title="Télécharger le raccourci bureau"
              >
                📱 Raccourci bureau
              </button>
              
              <button
                onClick={logout}
                className="px-4 py-2 rounded-md transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                  color: '#FFFFFF'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                Déconnexion
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="border-b" style={{borderColor: '#1A1E2E'}}>
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('account')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                style={{
                  borderBottomColor: activeTab === 'account' ? '#00C2FF' : 'transparent',
                  color: activeTab === 'account' ? '#00C2FF' : '#8A92B2'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'account') e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'account') e.currentTarget.style.color = '#8A92B2';
                }}
              >
                Mon compte
              </button>
              <button
                onClick={() => canAccessCemetery() ? setActiveTab('cemetery') : null}
                disabled={!canAccessCemetery()}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                style={{
                  borderBottomColor: activeTab === 'cemetery' ? '#00C2FF' : 'transparent',
                  color: activeTab === 'cemetery' ? '#00C2FF' : !canAccessCemetery() ? '#5B638C' : '#8A92B2',
                  cursor: !canAccessCemetery() ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'cemetery' && canAccessCemetery()) e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'cemetery' && canAccessCemetery()) e.currentTarget.style.color = '#8A92B2';
                }}
                title={
                  !hasRequiredFields() 
                    ? 'Veuillez compléter les informations de votre mairie' 
                    : profile?.subscription_expired 
                    ? 'Abonnement expiré - Contactez un administrateur'
                    : !profile?.subscription_active
                    ? 'Abonnement non actif - Contactez un administrateur'
                    : ''
                }
              >
                Gestion du cimetière
              </button>
              
              <button
                onClick={() => canAccessCemetery() ? setActiveTab('plan') : null}
                disabled={!canAccessCemetery()}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                style={{
                  borderBottomColor: activeTab === 'plan' ? '#00C2FF' : 'transparent',
                  color: activeTab === 'plan' ? '#00C2FF' : !canAccessCemetery() ? '#5B638C' : '#8A92B2',
                  cursor: !canAccessCemetery() ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'plan' && canAccessCemetery()) e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'plan' && canAccessCemetery()) e.currentTarget.style.color = '#8A92B2';
                }}
                title={
                  !hasRequiredFields() 
                    ? 'Veuillez compléter les informations de votre mairie' 
                    : profile?.subscription_expired 
                    ? 'Votre abonnement est expiré - contactez l\'administrateur' 
                    : ''
                }
              >
                Plan
              </button>
              
              <button
                onClick={isDemoMode ? null : () => setActiveTab('contact')}
                disabled={isDemoMode}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                style={{
                  borderBottomColor: activeTab === 'contact' ? '#00C2FF' : 'transparent',
                  color: isDemoMode ? '#5B638C' : (activeTab === 'contact' ? '#00C2FF' : '#8A92B2'),
                  cursor: isDemoMode ? 'not-allowed' : 'pointer',
                  opacity: isDemoMode ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDemoMode && activeTab !== 'contact') e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  if (!isDemoMode && activeTab !== 'contact') e.currentTarget.style.color = '#8A92B2';
                }}
                title={isDemoMode ? 'Désactivé en mode démo' : ''}
              >
                {isDemoMode ? '🔒 ' : ''}Contact / Support
              </button>
              
              {/* RGPD Tab - Discreet link */}
              <button
                onClick={isDemoMode ? null : () => setActiveTab('rgpd')}
                disabled={isDemoMode}
                className={`py-4 px-1 border-b-2 font-medium text-xs transition-colors duration-200`}
                style={{
                  borderBottomColor: activeTab === 'rgpd' ? '#00C2FF' : 'transparent',
                  color: isDemoMode ? '#3A4359' : (activeTab === 'rgpd' ? '#00C2FF' : '#5B638C'),
                  cursor: isDemoMode ? 'not-allowed' : 'pointer',
                  opacity: isDemoMode ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDemoMode && activeTab !== 'rgpd') e.currentTarget.style.color = '#8A92B2';
                }}
                onMouseLeave={(e) => {
                  if (!isDemoMode && activeTab !== 'rgpd') e.currentTarget.style.color = '#5B638C';
                }}
                title={isDemoMode ? 'Désactivé en mode démo' : ''}
              >
                🔒 RGPD
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'account' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* First login warning */}
        {user?.is_first_login === true && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid #FFC107',
            color: '#FFC107'
          }}>
            <strong>Bienvenue!</strong> Veuillez changer votre code d&apos;accès temporaire pour sécuriser votre compte.
          </div>
        )}

        {/* Warning if required fields are missing */}
        {!hasRequiredFields() && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(74, 91, 255, 0.1)',
            border: '1px solid #4A5BFF',
            color: '#00C2FF'
          }}>
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <strong>Information importante :</strong> Veuillez compléter les informations de votre mairie pour accéder à l&apos;outil de gestion du cimetière.
              </div>
            </div>
          </div>
        )}

        {/* Warning if no active subscription */}
        {profile && !profile.subscription_active && !profile.subscription_expired && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(74, 91, 255, 0.1)',
            border: '1px solid #4A5BFF',
            color: '#00C2FF'
          }}>
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <strong>Abonnement non actif :</strong> Votre abonnement n&apos;est pas actif. Veuillez contacter votre administrateur pour l&apos;activer.
              </div>
            </div>
          </div>
        )}

        {/* Warning if subscription is expiring soon (within 3 months) */}
        {profile && !profile.subscription_expired && profile.subscription_expiring_soon && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(255, 193, 7, 0.15)',
            border: '2px solid #FFC107',
            color: '#FFC107'
          }}>
            <div className="flex items-start">
              <svg className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <div className="font-bold text-lg mb-2">⚠️ Votre abonnement arrive bientôt à échéance</div>
                <p className="mb-2">
                  Votre abonnement expire dans <strong>{profile.days_until_expiry} jour{profile.days_until_expiry > 1 ? 's' : ''}</strong> 
                  {profile.subscription_expiry && ` (le ${new Date(profile.subscription_expiry).toLocaleDateString('fr-FR')})`}.
                </p>
                <p className="font-medium">
                  Veuillez contacter votre administrateur pour le renouveler afin de continuer à utiliser l&apos;outil de gestion du cimetière.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Warning if subscription is expired */}
        {profile?.subscription_expired && (
          <div className="px-4 py-3 rounded mb-6" style={{
            backgroundColor: 'rgba(255, 87, 87, 0.1)',
            border: '2px solid #FF5757',
            color: '#FF5757'
          }}>
            <div className="flex items-start">
              <svg className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <div className="font-bold text-lg mb-2">❌ Abonnement Expiré</div>
                <p>
                  Votre abonnement a expiré. Veuillez contacter votre administrateur pour le renouveler.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* RGPD Consent Expiration Notification */}
        <RGPDConsentNotification 
          onRenewConsent={() => setActiveTab('rgpd')}
        />

        {/* Demo Mode Banner */}
        {isDemoMode && (
          <div
            style={{
              backgroundColor: 'rgba(255, 184, 0, 0.15)',
              border: '1px solid #FFB800',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <span style={{ fontSize: '24px' }}>🎭</span>
            <div>
              <p style={{ color: '#FFB800', fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>
                Mode Démonstration
              </p>
              <p style={{ color: '#B8C0E0', fontSize: '13px', lineHeight: '1.5' }}>
                Vous êtes en mode démo. Vous pouvez explorer toutes les fonctionnalités, mais les modifications ne seront pas enregistrées.
                La génération de PDF reste disponible.
              </p>
            </div>
          </div>
        )}

        {/* Profile Information */}
        <div className="rounded-lg shadow-lg p-6 mb-6" style={{backgroundColor: '#2C3148'}}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium" style={{color: '#FFFFFF'}}>Informations du Compte</h2>
            {!isEditingProfile && !isDemoMode && (
              <button
                onClick={() => {
                  setIsEditingProfile(true);
                  // Clear non-error messages when entering edit mode
                  if (emailMessage.type !== 'error') {
                    setEmailMessage({ type: '', text: '' });
                  }
                }}
                className="text-sm font-medium transition-colors duration-200"
                style={{color: '#00C2FF'}}
                onMouseEnter={(e) => e.currentTarget.style.color = '#4A5BFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#00C2FF'}
              >
                ✏️ Modifier
              </button>
            )}
          </div>
          
          {profile && (
            <div className="space-y-6">
              {/* Editable email field */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{color: '#8A92B2'}}>Email</label>
                  <div className="flex items-start gap-3">
                    {isEditingProfile ? (
                      <input
                        type="email"
                        value={editedProfile.email}
                        onChange={(e) => {
                          setEditedProfile({...editedProfile, email: e.target.value});
                          // Clear message when user starts typing again
                          if (emailMessage.text) {
                            setEmailMessage({ type: '', text: '' });
                          }
                        }}
                        onBlur={(e) => {
                          // Validate on blur (when user leaves field)
                          const validation = validateEmailRealtime(e.target.value);
                          if (!validation.valid && validation.message) {
                            setEmailMessage({ type: 'error', text: validation.message });
                          }
                        }}
                        className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#242A3D',
                          borderColor: emailMessage.type === 'error' ? '#FF5757' : '#4A5BFF',
                          color: '#FFFFFF'
                        }}
                        placeholder="votre.email@exemple.fr"
                      />
                    ) : (
                      <div className="flex-1">
                        <p className="text-sm" style={{color: '#FFFFFF'}}>{profile.email}</p>
                      </div>
                    )}
                    
                    {/* Message displayed both in edit and read mode */}
                    {emailMessage.text && (
                      <div className="flex-1 flex items-start gap-2 px-3 py-2 rounded-md" style={{
                        backgroundColor: emailMessage.type === 'error' ? 'rgba(255, 87, 87, 0.1)' : 
                                       emailMessage.type === 'success' ? 'rgba(0, 255, 179, 0.1)' : 
                                       'rgba(0, 194, 255, 0.1)',
                        border: `1px solid ${emailMessage.type === 'error' ? '#FF5757' : 
                                             emailMessage.type === 'success' ? '#00FFB3' : 
                                             '#00C2FF'}`
                      }}>
                        <span className="text-lg">
                          {emailMessage.type === 'error' ? '❌' : 
                           emailMessage.type === 'success' ? '✅' : 
                           '📧'}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm" style={{
                            color: emailMessage.type === 'error' ? '#FF5757' : 
                                   emailMessage.type === 'success' ? '#00FFB3' : 
                                   '#00C2FF'
                          }}>
                            {emailMessage.text}
                          </p>
                          {/* Dismiss button for non-error messages */}
                          {emailMessage.type !== 'error' && (
                            <button
                              onClick={() => setEmailMessage({ type: '', text: '' })}
                              className="mt-2 text-xs underline hover:no-underline"
                              style={{
                                color: emailMessage.type === 'success' ? '#00FFB3' : '#00C2FF'
                              }}
                            >
                              Fermer
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Read-only fields (dates and subscription) - hidden for demo */}
              {!isDemoMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t" style={{borderColor: '#1C2133'}}>
                <div>
                  <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>Compte créé le</label>
                  <p className="mt-1 text-sm" style={{color: '#FFFFFF'}}>
                    {new Date(profile.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                {profile.subscription_start && (
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>Date de début d&apos;abonnement</label>
                    <p className="mt-1 text-sm" style={{color: '#FFFFFF'}}>
                      {new Date(profile.subscription_start).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                
                {profile.subscription_expiry && (
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>Date d&apos;expiration d&apos;abonnement</label>
                    <p className="mt-1 text-sm" style={{color: '#FFFFFF'}}>
                      {new Date(profile.subscription_expiry).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>Statut de l&apos;abonnement</label>
                  <p className="mt-1 text-sm font-medium" style={{
                    color: profile.subscription_expired ? '#FFC107' : 
                           profile.subscription_active ? '#00FFB3' : '#8A92B2'
                  }}>
                    {profile.subscription_expired ? 'Expiré' : 
                     profile.subscription_active ? 'Actif' : 'Aucun abonnement'}
                  </p>
                </div>
              </div>
              )}

              {/* Required editable fields section */}
              <div className="pt-6 border-t" style={{borderColor: '#1C2133'}}>
                <h3 className="text-md font-medium mb-4" style={{color: '#FFFFFF'}}>
                  Informations de la Mairie 
                  <span className="text-sm ml-2" style={{color: '#FF5757'}}>* Requis</span>
                </h3>
                
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>
                      Adresse Mairie <span style={{color: '#FF5757'}}>*</span>
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={editedProfile.city_hall_address}
                        onChange={(e) => setEditedProfile({...editedProfile, city_hall_address: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#242A3D',
                          borderColor: '#4A5BFF',
                          color: '#FFFFFF'
                        }}
                        placeholder="123 Rue de la Mairie, 75001 Paris"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic" style={{
                        color: profile.city_hall_address ? '#FFFFFF' : '#FF5757'
                      }}>
                        {profile.city_hall_address || 'Non renseigné'}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>
                      Nom du Maire <span style={{color: '#FF5757'}}>*</span>
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={editedProfile.mayor_name}
                        onChange={(e) => setEditedProfile({...editedProfile, mayor_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#242A3D',
                          borderColor: '#4A5BFF',
                          color: '#FFFFFF'
                        }}
                        placeholder="Jean Dupont"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic" style={{
                        color: profile.mayor_name ? '#FFFFFF' : '#FF5757'
                      }}>
                        {profile.mayor_name || 'Non renseigné'}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>
                      Nom de la secrétaire de mairie <span style={{color: '#FF5757'}}>*</span>
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={editedProfile.secretary_name}
                        onChange={(e) => setEditedProfile({...editedProfile, secretary_name: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#242A3D',
                          borderColor: '#4A5BFF',
                          color: '#FFFFFF'
                        }}
                        placeholder="Marie Martin"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic" style={{
                        color: profile.secretary_name ? '#FFFFFF' : '#FF5757'
                      }}>
                        {profile.secretary_name || 'Non renseigné'}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium" style={{color: '#8A92B2'}}>
                      Numéro de téléphone <span style={{color: '#FF5757'}}>*</span>
                    </label>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        value={editedProfile.phone_number}
                        onChange={(e) => setEditedProfile({...editedProfile, phone_number: e.target.value})}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: '#242A3D',
                          borderColor: '#4A5BFF',
                          color: '#FFFFFF'
                        }}
                        placeholder="01 23 45 67 89"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic" style={{
                        color: profile.phone_number ? '#FFFFFF' : '#FF5757'
                      }}>
                        {profile.phone_number || 'Non renseigné'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6 flex space-x-4">
            {isEditingProfile ? (
              <>
                <button
                  onClick={isDemoMode ? null : handleSaveProfile}
                  disabled={isDemoMode}
                  className="px-4 py-2 rounded-md transition-all duration-300"
                  style={{
                    background: isDemoMode 
                      ? 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)' 
                      : 'linear-gradient(135deg, #00FFB3 0%, #00C2FF 100%)',
                    color: isDemoMode ? '#8A8A8A' : '#1A1E2E',
                    cursor: isDemoMode ? 'not-allowed' : 'pointer',
                    opacity: isDemoMode ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isDemoMode) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 255, 179, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                  title={isDemoMode ? 'Fonctionnalité désactivée en mode démo' : ''}
                >
                  {isDemoMode ? '🔒 Enregistrer' : '💾 Enregistrer'}
                </button>
                <button
                  onClick={handleCancelEditProfile}
                  className="px-4 py-2 rounded-md transition-all duration-300"
                  style={{
                    backgroundColor: '#1C2133',
                    color: '#8A92B2',
                    border: '1px solid #4A5BFF'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#242A3D';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#1C2133';
                    e.currentTarget.style.color = '#8A92B2';
                  }}
                >
                  Annuler
                </button>
              </>
            ) : (
              !isDemoMode && (
              <button
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 rounded-md transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                  color: '#FFFFFF'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                Changer le mot de passe
              </button>
              )
            )}
          </div>
        </div>

        {/* Subscription Status - hidden for demo */}
        {!isDemoMode && (
        <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
          <h2 className="text-lg font-medium mb-4" style={{color: '#FFFFFF'}}>Statut de l&apos;Abonnement</h2>
          
          {profile?.subscription_expired ? (
            <div className="flex items-center p-4 rounded-lg" style={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid #FFC107'
            }}>
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <p className="font-medium" style={{color: '#FFC107'}}>Abonnement Expiré</p>
                <p className="text-sm" style={{color: '#FFC107'}}>
                  Votre abonnement a expiré le {new Date(profile.subscription_expiry).toLocaleDateString('fr-FR')}.
                  Contactez un administrateur pour le renouveler.
                </p>
              </div>
            </div>
          ) : profile?.subscription_active ? (
            <div className="flex items-center p-4 rounded-lg" style={{
              backgroundColor: 'rgba(0, 255, 179, 0.1)',
              border: '1px solid #00FFB3'
            }}>
              <span className="text-2xl mr-3">✅</span>
              <div>
                <p className="font-medium" style={{color: '#00FFB3'}}>Abonnement Actif</p>
                <p className="text-sm" style={{color: '#00FFB3'}}>
                  Votre abonnement est valide jusqu&apos;au {new Date(profile.subscription_expiry).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center p-4 rounded-lg" style={{
              backgroundColor: 'rgba(138, 146, 178, 0.1)',
              border: '1px solid #8A92B2'
            }}>
              <span className="text-2xl mr-3">ℹ️</span>
              <div>
                <p className="font-medium" style={{color: '#8A92B2'}}>Aucun Abonnement</p>
                <p className="text-sm" style={{color: '#8A92B2'}}>
                  Contactez un administrateur pour activer votre abonnement.
                </p>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      )}

      {/* Cemetery Management Tab */}
      {activeTab === 'cemetery' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-lg shadow-lg" style={{backgroundColor: '#2C3148'}}>
            <div className="p-6 border-b" style={{borderColor: '#1C2133'}}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium" style={{color: '#FFFFFF'}}>Registre des Concessions</h2>
              </div>
              
              <div className="flex justify-between items-center">
                {/* Search Bar with Autocomplete and Tip Button - Left Side */}
                <div className="flex items-center gap-3">
                  <div className="relative" style={{width: '300px'}}>
                    <input
                      type="text"
                      placeholder="Rechercher par nom, prénom..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full px-4 py-2 rounded-md border focus:outline-none focus:ring-2 transition-all duration-200"
                      style={{
                        backgroundColor: '#1C2133',
                        borderColor: '#3A4359',
                        color: '#FFFFFF',
                        paddingLeft: '40px'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#4A5BFF';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(74, 91, 255, 0.1)';
                        if (searchQuery) generateSuggestions(searchQuery);
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#3A4359';
                        e.currentTarget.style.boxShadow = '';
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                    />
                    <svg 
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" 
                      style={{color: '#8A92B2'}}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSearchSuggestions([]);
                          setShowSuggestions(false);
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        style={{color: '#8A92B2'}}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#FF5757'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#8A92B2'}
                      >
                        ✕
                      </button>
                    )}
                    
                    {/* Autocomplete Dropdown */}
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#2C3148',
                        border: '1px solid #3A4359',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {searchSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: index < searchSuggestions.length - 1 ? '1px solid #1C2133' : 'none',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(74, 91, 255, 0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div style={{color: '#FFFFFF', fontSize: '14px', fontWeight: '500', marginBottom: '2px'}}>
                              {suggestion.name}
                            </div>
                            <div style={{color: '#8A92B2', fontSize: '12px'}}>
                              {suggestion.concessionInfo}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Guides funéraires tip button */}
                  <a
                    href="https://www.collectivites-locales.gouv.fr/animer-les-territoires/services-la-population/droit-funeraire/les-guides-funeraires"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #3A4359',
                      color: '#8A92B2',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4A5BFF';
                      e.currentTarget.style.color = '#4A5BFF';
                      e.currentTarget.style.backgroundColor = 'rgba(74, 91, 255, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#3A4359';
                      e.currentTarget.style.color = '#8A92B2';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Ouvrir les guides funéraires dans un nouvel onglet"
                  >
                    <span style={{fontSize: '14px'}}>💡</span>
                    <span>Guides funéraires</span>
                  </a>
                  
                  {/* Service juridique button */}
                  <a
                    href="https://www.larrat-avocat.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #3A4359',
                      color: '#8A92B2',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#D4A853';
                      e.currentTarget.style.color = '#D4A853';
                      e.currentTarget.style.backgroundColor = 'rgba(212, 168, 83, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#3A4359';
                      e.currentTarget.style.color = '#8A92B2';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Conseil juridique - Cabinet Larrat Avocat"
                  >
                    <span style={{fontSize: '14px'}}>⚖️</span>
                    <span>Service juridique</span>
                  </a>
                </div>

                {/* Action Buttons - Right Side */}
                <div className="flex gap-3">
                  {/* Import Excel Button */}
                  <label 
                    className="px-4 py-2 rounded-md transition-all duration-300"
                    style={{
                      background: isDemoMode 
                        ? 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)'
                        : 'linear-gradient(135deg, #00FFB3 0%, #00C2FF 100%)',
                      color: isDemoMode ? '#8A8A8A' : '#1A1E2E',
                      cursor: isDemoMode ? 'not-allowed' : 'pointer',
                      opacity: isDemoMode ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isDemoMode) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 255, 179, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    title={isDemoMode ? 'Fonctionnalité désactivée en mode démo' : ''}
                  >
                    {isDemoMode ? '🔒 Importer depuis Excel' : '📊 Importer depuis Excel'}
                    <input
                      type="file"
                      accept=".xlsx,.xls,.xlsm"
                      onChange={isDemoMode ? null : handleExcelImport}
                      disabled={isDemoMode}
                      style={{display: 'none'}}
                    />
                  </label>
                  
                  {/* Add Concession Button */}
                  <button
                    onClick={handleAddConcession}
                    className="px-4 py-2 rounded-md transition-all duration-300"
                    style={{
                      background: 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                      color: '#FFFFFF',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    + Ajouter une concession
                  </button>
                </div>
              </div>
            </div>

            {/* Statistics Block */}
            <div className="mb-4 flex gap-4 px-2 justify-between items-center">
              <div className="flex gap-4">
                <div className="px-4 py-2 rounded-md" style={{
                  backgroundColor: 'rgba(0, 194, 255, 0.1)',
                  border: '1px solid rgba(0, 194, 255, 0.2)'
                }}>
                  <span style={{color: '#8A92B2', fontSize: '12px', fontWeight: '500'}}>Total concessions : </span>
                  <span style={{color: '#00C2FF', fontSize: '14px', fontWeight: '700'}}>{concessions.length}</span>
                </div>
                <div className="px-4 py-2 rounded-md" style={{
                  backgroundColor: 'rgba(0, 255, 179, 0.1)',
                  border: '1px solid rgba(0, 255, 179, 0.2)'
                }}>
                  <span style={{color: '#8A92B2', fontSize: '12px', fontWeight: '500'}}>Total personnes : </span>
                  <span style={{color: '#00FFB3', fontSize: '14px', fontWeight: '700'}}>
                    {concessions.reduce((total, c) => total + c.persons.length, 0)}
                  </span>
                </div>
              </div>
              
              {/* Print Button */}
              <button
                onClick={handlePrintList}
                className="px-4 py-2 rounded-md transition-all duration-300 flex items-center gap-2"
                style={{
                  backgroundColor: 'rgba(138, 146, 178, 0.15)',
                  color: '#8A92B2',
                  border: '1px solid rgba(138, 146, 178, 0.3)',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(138, 146, 178, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(138, 146, 178, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                title="Imprimer la liste des personnes"
              >
                🖨️ Imprimer la liste
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{borderColor: '#1C2133'}}>
                <thead style={{backgroundColor: '#1C2133'}}>
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2', width: '60px'}}>
                      #
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'num_plan' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('num_plan')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        N° Plan
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'num_plan' ? 1 : 0.3}}>
                          {sortColumn === 'num_plan' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'acheteur' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('acheteur')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Acheteur
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'acheteur' ? 1 : 0.3}}>
                          {sortColumn === 'acheteur' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'num_concession' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('num_concession')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        N° Conc.
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'num_concession' ? 1 : 0.3}}>
                          {sortColumn === 'num_concession' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'date_achat' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('date_achat')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Date Achat
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'date_achat' ? 1 : 0.3}}>
                          {sortColumn === 'date_achat' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'duree_concession' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('duree_concession')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Durée
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'duree_concession' ? 1 : 0.3}}>
                          {sortColumn === 'duree_concession' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'jours_restants' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('jours_restants')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Jours
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'jours_restants' ? 1 : 0.3}}>
                          {sortColumn === 'jours_restants' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'date_fin' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('date_fin')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Date FC/CP
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'date_fin' ? 1 : 0.3}}>
                          {sortColumn === 'date_fin' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th 
                      className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors duration-200" 
                      style={{color: sortColumn === 'nombre_places' ? '#4A5BFF' : '#8A92B2'}}
                      onClick={() => handleSort('nombre_places')}
                    >
                      <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        Places
                        <span style={{width: '10px', textAlign: 'center', opacity: sortColumn === 'nombre_places' ? 1 : 0.3}}>
                          {sortColumn === 'nombre_places' ? (sortDirection === 'asc' ? '▲' : '▼') : '▲'}
                        </span>
                      </span>
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider" style={{color: '#8A92B2'}}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{backgroundColor: '#2C3148', borderColor: '#1C2133'}}>
                  {getSortedConcessions().map((concession, index) => {
                    const remainingDays = calculateRemainingDays(concession.date_achat, concession.duree_concession);
                    const daysColor = getRemainingDaysColor(remainingDays);
                    const isExpanded = expandedConcessions.has(concession.id);
                    const occupancy = concession.persons.length;
                    const capacity = concession.nombre_places || 1;
                    const isFull = occupancy >= capacity;

                    return (
                      <React.Fragment key={concession.id}>
                      <tr 
                        className="cursor-pointer transition-all duration-200"
                        style={{backgroundColor: isExpanded ? '#1C2133' : '#2C3148'}}
                        onClick={() => toggleConcession(concession.id)}
                      >
                        <td className="px-2 py-2 text-sm text-center" style={{color: '#8A92B2', fontWeight: '500'}}>
                          {index + 1}
                        </td>
                        <td className="px-2 py-2 text-sm font-medium">
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md" style={{
                            backgroundColor: 'rgba(0, 194, 255, 0.1)',
                            color: '#00C2FF',
                            border: '1px solid rgba(0, 194, 255, 0.2)'
                          }}>
                            <span style={{fontSize: '10px'}}>{isExpanded ? '▼' : '▶'}</span>
                            <span className="font-semibold">{concession.num_plan || '-'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-sm" style={{color: '#B8C0E0'}}>{concession.acheteur || '-'}</td>
                        <td className="px-2 py-2 text-sm" style={{color: '#B8C0E0'}}>{concession.num_concession || '-'}</td>
                        <td className="px-2 py-2 text-sm" style={{color: '#8A92B2'}}>
                          {concession.date_achat ? new Date(concession.date_achat).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-2 py-2 text-sm" style={{color: '#B8C0E0'}}>{concession.duree_concession || '-'}</td>
                        <td className="px-2 py-2">
                          <div className="px-3 py-1 rounded-md text-center text-sm font-semibold" style={{
                            backgroundColor: remainingDays === '∞' ? 'rgba(138, 146, 178, 0.15)' : 
                                           (typeof remainingDays === 'number' && remainingDays <= 0) ? 'rgba(255, 87, 87, 0.2)' :
                                           (typeof remainingDays === 'number' && remainingDays <= 365) ? 'rgba(255, 193, 7, 0.2)' :
                                           'rgba(0, 255, 179, 0.2)',
                            color: remainingDays === '∞' ? '#8A92B2' : 
                                   (typeof remainingDays === 'number' && remainingDays <= 0) ? '#FF8A8A' :
                                   (typeof remainingDays === 'number' && remainingDays <= 365) ? '#FFD54F' :
                                   '#00FFB3',
                            border: `1px solid ${remainingDays === '∞' ? 'rgba(138, 146, 178, 0.3)' : 
                                   (typeof remainingDays === 'number' && remainingDays <= 0) ? 'rgba(255, 87, 87, 0.4)' :
                                   (typeof remainingDays === 'number' && remainingDays <= 365) ? 'rgba(255, 193, 7, 0.4)' :
                                   'rgba(0, 255, 179, 0.4)'}`
                          }}>
                            {remainingDays !== null ? (remainingDays === '∞' ? '∞' : `${remainingDays}j`) : '-'}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-xs" style={{color: '#8A92B2'}}>
                          {calculateEndDate(concession.date_achat, concession.duree_concession) || '-'}
                        </td>
                        <td className="px-2 py-2">
                          <div className="px-3 py-1 rounded-md text-center text-sm font-semibold" style={{
                            backgroundColor: isFull ? 'rgba(255, 87, 87, 0.2)' : 
                                           (occupancy > 0 ? 'rgba(255, 193, 7, 0.2)' : 
                                           'rgba(0, 255, 179, 0.2)'),
                            color: isFull ? '#FF8A8A' : 
                                   (occupancy > 0 ? '#FFD54F' : '#00FFB3'),
                            border: `1px solid ${isFull ? 'rgba(255, 87, 87, 0.4)' : 
                                   (occupancy > 0 ? 'rgba(255, 193, 7, 0.4)' : 
                                   'rgba(0, 255, 179, 0.4)')}`
                          }}>
                            {occupancy}/{capacity}
                          </div>
                        </td>
                        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <button onClick={(e) => { e.stopPropagation(); handleEditConcession(concession); }}
                              className="px-3 py-1 text-xs rounded-md font-medium transition-all duration-200"
                              style={{
                                backgroundColor: 'rgba(0, 194, 255, 0.15)',
                                color: '#00C2FF',
                                border: '1px solid rgba(0, 194, 255, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.25)';
                                e.currentTarget.style.borderColor = '#00C2FF';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.15)';
                                e.currentTarget.style.borderColor = 'rgba(0, 194, 255, 0.3)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}>
                              ✏️ Modifier
                            </button>
                            <button 
                              onClick={isDemoMode ? (e) => e.stopPropagation() : (e) => { e.stopPropagation(); handleDeleteConcession(concession.id); }}
                              disabled={isDemoMode}
                              className="px-3 py-1 text-xs rounded-md font-medium transition-all duration-200"
                              style={{
                                backgroundColor: isDemoMode ? 'rgba(128, 128, 128, 0.15)' : 'rgba(255, 87, 87, 0.15)',
                                color: isDemoMode ? '#8A8A8A' : '#FF8A8A',
                                border: isDemoMode ? '1px solid rgba(128, 128, 128, 0.3)' : '1px solid rgba(255, 87, 87, 0.3)',
                                cursor: isDemoMode ? 'not-allowed' : 'pointer',
                                opacity: isDemoMode ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!isDemoMode) {
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.25)';
                                  e.currentTarget.style.borderColor = '#FF5757';
                                  e.currentTarget.style.color = '#FF5757';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isDemoMode) {
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.15)';
                                  e.currentTarget.style.borderColor = 'rgba(255, 87, 87, 0.3)';
                                  e.currentTarget.style.color = '#FF8A8A';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }
                              }}
                              title={isDemoMode ? 'Fonctionnalité désactivée en mode démo' : ''}>
                              {isDemoMode ? '🔒' : '🗑️'} Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="10" className="p-0">
                            <div style={{
                              backgroundColor: '#151822',
                              borderLeft: '4px solid #00C2FF',
                              borderBottom: '2px solid #2C3148',
                              margin: '0 8px 12px 8px',
                              borderRadius: '0 8px 8px 0',
                              boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.3)'
                            }}>
                              {/* Header avec identification de la concession */}
                              <div className="px-6 py-4" style={{
                                background: 'linear-gradient(90deg, rgba(0, 194, 255, 0.15) 0%, transparent 100%)',
                                borderBottom: '1px solid rgba(0, 194, 255, 0.2)'
                              }}>
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                      <span style={{
                                        backgroundColor: 'rgba(0, 194, 255, 0.2)',
                                        color: '#00C2FF',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        border: '1px solid rgba(0, 194, 255, 0.4)'
                                      }}>
                                        📍 {concession.num_plan || 'N/A'}
                                      </span>
                                      <span style={{color: '#5B638C', fontSize: '12px'}}>•</span>
                                      <span style={{color: '#8A92B2', fontSize: '13px'}}>{concession.acheteur || 'Sans nom'}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span style={{
                                      backgroundColor: isFull ? 'rgba(255, 87, 87, 0.15)' : 'rgba(0, 255, 179, 0.15)',
                                      color: isFull ? '#FF8A8A' : '#00FFB3',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: '500'
                                    }}>
                                      {occupancy}/{capacity} places
                                    </span>
                                    {!isFull && (
                                      <button onClick={() => handleAddPerson(concession)}
                                        className="px-4 py-2 text-xs rounded-md font-medium transition-all duration-200"
                                        style={{
                                          backgroundColor: 'rgba(0, 255, 179, 0.15)',
                                          color: '#00FFB3',
                                          border: '1px solid rgba(0, 255, 179, 0.4)'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.25)';
                                          e.currentTarget.style.borderColor = '#00FFB3';
                                          e.currentTarget.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.15)';
                                          e.currentTarget.style.borderColor = 'rgba(0, 255, 179, 0.4)';
                                          e.currentTarget.style.transform = 'translateY(0)';
                                        }}>
                                        + Ajouter une personne
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Contenu - Liste des personnes */}
                              <div className="px-6 py-4">
                                {concession.persons.length === 0 ? (
                                  <div className="text-center py-6" style={{
                                    backgroundColor: 'rgba(138, 146, 178, 0.05)',
                                    borderRadius: '8px',
                                    border: '1px dashed rgba(138, 146, 178, 0.2)'
                                  }}>
                                    <span style={{fontSize: '24px', opacity: 0.5}}>👤</span>
                                    <p style={{color: '#8A92B2', marginTop: '8px'}}>Aucune personne enregistrée</p>
                                  </div>
                                ) : (
                                  <table className="w-full" style={{borderCollapse: 'separate', borderSpacing: '0 4px'}}>
                                    <thead>
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{color: '#5B638C', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Nom</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{color: '#5B638C', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Prénom</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold" style={{color: '#5B638C', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Date MET</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold" style={{color: '#5B638C', textTransform: 'uppercase', letterSpacing: '0.5px'}}>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {concession.persons.map((person, personIndex) => (
                                        <tr key={person.id} style={{
                                          backgroundColor: personIndex % 2 === 0 ? 'rgba(44, 49, 72, 0.5)' : 'rgba(44, 49, 72, 0.25)',
                                          borderRadius: '6px'
                                        }}>
                                          <td className="px-3 py-3 text-sm font-medium" style={{color: '#FFFFFF', borderRadius: '6px 0 0 6px'}}>{person.nom}</td>
                                          <td className="px-3 py-3 text-sm" style={{color: '#B8C0E0'}}>{person.prenom || '-'}</td>
                                          <td className="px-3 py-3 text-sm" style={{color: '#B8C0E0'}}>
                                            {person.date_met ? new Date(person.date_met).toLocaleDateString('fr-FR') : '-'}
                                          </td>
                                          <td className="px-3 py-3 text-right" style={{borderRadius: '0 6px 6px 0'}}>
                                            <button onClick={() => handleEditPerson(person, concession)}
                                              className="px-3 py-1.5 text-xs rounded-md font-medium transition-all duration-200 mr-2"
                                              style={{
                                                backgroundColor: 'rgba(0, 255, 179, 0.15)',
                                                color: '#00FFB3',
                                                border: '1px solid rgba(0, 255, 179, 0.3)'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.25)';
                                                e.currentTarget.style.borderColor = '#00FFB3';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0, 255, 179, 0.15)';
                                                e.currentTarget.style.borderColor = 'rgba(0, 255, 179, 0.3)';
                                              }}>
                                              ✏️ Modifier
                                            </button>
                                            <button onClick={isDemoMode ? null : () => handleDeletePerson(person, concession)}
                                              disabled={isDemoMode}
                                              className="px-3 py-1.5 text-xs rounded-md font-medium transition-all duration-200"
                                              style={{
                                                backgroundColor: isDemoMode ? 'rgba(128, 128, 128, 0.15)' : 'rgba(255, 87, 87, 0.15)',
                                                color: isDemoMode ? '#8A8A8A' : '#FF8A8A',
                                                border: isDemoMode ? '1px solid rgba(128, 128, 128, 0.3)' : '1px solid rgba(255, 87, 87, 0.3)',
                                                cursor: isDemoMode ? 'not-allowed' : 'pointer',
                                                opacity: isDemoMode ? 0.6 : 1
                                              }}
                                              onMouseEnter={(e) => {
                                                if (!isDemoMode) {
                                                  e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.25)';
                                                  e.currentTarget.style.borderColor = '#FF5757';
                                                  e.currentTarget.style.color = '#FF5757';
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isDemoMode) {
                                                  e.currentTarget.style.backgroundColor = 'rgba(255, 87, 87, 0.15)';
                                                  e.currentTarget.style.borderColor = 'rgba(255, 87, 87, 0.3)';
                                                  e.currentTarget.style.color = '#FF8A8A';
                                                }
                                              }}>
                                              {isDemoMode ? '🔒' : '🗑️'} Supprimer
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {concessions.length === 0 && (
                <div className="text-center py-12" style={{color: '#8A92B2'}}>
                  <p>Aucune concession enregistrée.</p>
                  <p className="text-sm mt-2">Cliquez sur &quot;+ Ajouter une concession&quot; pour commencer.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plan Tab */}
      {activeTab === 'plan' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-lg shadow-lg" style={{backgroundColor: '#2C3148'}}>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6" style={{color: '#FFFFFF', borderBottom: '2px solid #00C2FF', paddingBottom: '12px'}}>
                📍 Plan du cimetière
              </h2>

              {planLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-t-2" style={{borderColor: '#00C2FF'}}></div>
                  <p className="mt-4" style={{color: '#8A92B2'}}>Chargement du plan...</p>
                </div>
              ) : !cemeteryPlan ? (
                <div className="text-center py-12" style={{backgroundColor: '#1A1E2E', borderRadius: '12px', border: '1px solid #3A4359'}}>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>📄</div>
                  <p style={{color: '#8A92B2', fontSize: '16px'}}>
                    Aucun plan disponible
                  </p>
                  <p style={{color: '#5B638C', fontSize: '14px', marginTop: '8px'}}>
                    Contactez l&apos;administrateur pour obtenir le plan de votre cimetière
                  </p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p style={{color: '#B8C0E0', fontSize: '14px'}}>
                        <span style={{color: '#8A92B2'}}>Fichier : </span>
                        {cemeteryPlan.original_filename}
                      </p>
                      <p style={{color: '#5B638C', fontSize: '12px', marginTop: '4px'}}>
                        Mis à jour le {new Date(cemeteryPlan.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('access_token');
                          const response = await axios.get(`${API}/client/cemetery-plan/file`, {
                            responseType: 'blob',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          
                          // Create download link
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', cemeteryPlan.original_filename);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Error downloading plan:', error);
                          setMessageModal({
                            isOpen: true,
                            title: 'Erreur',
                            message: 'Erreur lors du téléchargement du plan',
                            type: 'error'
                          });
                        }
                      }}
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: 'rgba(0, 194, 255, 0.15)',
                        color: '#00C2FF',
                        border: '1px solid rgba(0, 194, 255, 0.3)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.25)';
                        e.currentTarget.style.borderColor = '#00C2FF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 194, 255, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(0, 194, 255, 0.3)';
                      }}
                    >
                      📥 Télécharger
                    </button>
                  </div>

                  {/* Plan Viewer */}
                  <div style={{
                    backgroundColor: '#1C2133',
                    borderRadius: '12px',
                    border: '2px solid #3A4359',
                    overflow: 'hidden',
                    minHeight: '600px'
                  }}>
                    <PlanViewer 
                      fileUrl={`${API}/client/cemetery-plan/file`}
                      fileType={cemeteryPlan.file_type}
                      fileName={cemeteryPlan.original_filename}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact / Support Tab */}
      {activeTab === 'contact' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-lg shadow-lg" style={{backgroundColor: '#2C3148'}}>
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#FFFFFF'}}>
                Contact – Support technique
              </h2>
              
              <p className="mb-6" style={{color: '#8A92B2', lineHeight: '1.6'}}>
                Vous pouvez nous contacter pour toute question, suggestion ou problème rencontré. 
                Nous vous répondrons dans les plus brefs délais.
              </p>

              {/* Success/Error Message */}
              {contactMessage.text && (
                <div 
                  className="mb-6 px-4 py-3 rounded-md"
                  style={{
                    backgroundColor: contactMessage.type === 'success' ? 'rgba(0, 255, 179, 0.1)' : 'rgba(255, 87, 87, 0.1)',
                    border: `1px solid ${contactMessage.type === 'success' ? '#00FFB3' : '#FF5757'}`,
                    color: contactMessage.type === 'success' ? '#00FFB3' : '#FF5757'
                  }}
                >
                  {contactMessage.text}
                </div>
              )}

              {/* Contact Form */}
              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  setContactSubmitting(true);
                  setContactMessage({ type: '', text: '' });

                  try {
                    const formData = new FormData();
                    formData.append('request_type', contactForm.requestType);
                    formData.append('email', contactForm.email || profile?.email);
                    formData.append('subject', contactForm.subject);
                    formData.append('message', contactForm.message);
                    if (contactForm.attachment) {
                      formData.append('attachment', contactForm.attachment);
                    }

                    const response = await axios.post(
                      `${API}/client/contact-support`,
                      formData,
                      {
                        headers: {
                          'Content-Type': 'multipart/form-data',
                          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                        }
                      }
                    );

                    setContactMessage({
                      type: 'success',
                      text: response.data.message
                    });

                    // Reset form
                    setContactForm({
                      requestType: 'Question',
                      email: profile?.email || '',
                      subject: '',
                      message: '',
                      attachment: null
                    });

                    // Scroll to top to see message
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                  } catch (error) {
                    console.error('Error sending contact form:', error);
                    setContactMessage({
                      type: 'error',
                      text: extractErrorMessage(error, 'Erreur lors de l\'envoi du message. Veuillez réessayer.')
                    });
                  } finally {
                    setContactSubmitting(false);
                  }
                }}
                className="space-y-6"
              >
                {/* Request Type */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#8A92B2'}}>
                    Type de demande
                  </label>
                  <select
                    value={contactForm.requestType}
                    onChange={(e) => setContactForm({...contactForm, requestType: e.target.value})}
                    required
                    className="w-full px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    style={{
                      backgroundColor: '#1C2133',
                      borderColor: '#3A4359',
                      color: '#FFFFFF'
                    }}
                  >
                    <option value="Suggestion">Suggestion</option>
                    <option value="Question">Question</option>
                    <option value="Problème technique">Problème technique</option>
                    <option value="Demande urgente">Demande urgente</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#8A92B2'}}>
                    Adresse email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email || profile?.email || ''}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                    className="w-full px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    style={{
                      backgroundColor: '#1C2133',
                      borderColor: '#3A4359',
                      color: '#FFFFFF'
                    }}
                    placeholder="votre.email@example.com"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#8A92B2'}}>
                    Sujet
                  </label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    required
                    className="w-full px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    style={{
                      backgroundColor: '#1C2133',
                      borderColor: '#3A4359',
                      color: '#FFFFFF'
                    }}
                    placeholder="Brève description de votre demande"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#8A92B2'}}>
                    Message
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    rows={8}
                    className="w-full px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    style={{
                      backgroundColor: '#1C2133',
                      borderColor: '#3A4359',
                      color: '#FFFFFF',
                      resize: 'vertical'
                    }}
                    placeholder="Décrivez votre demande en détail..."
                  />
                </div>

                {/* File Attachment */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#8A92B2'}}>
                    Pièce jointe (optionnel)
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setContactForm({...contactForm, attachment: e.target.files[0]})}
                    className="w-full px-4 py-2 rounded-md border transition-all duration-200"
                    style={{
                      backgroundColor: '#1C2133',
                      borderColor: '#3A4359',
                      color: '#8A92B2'
                    }}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <p className="text-xs mt-1" style={{color: '#5B638C'}}>
                    Formats acceptés : images, PDF, documents Word
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={contactSubmitting}
                    className="px-6 py-3 rounded-md font-medium transition-all duration-300"
                    style={{
                      background: contactSubmitting ? '#3A4359' : 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)',
                      color: '#FFFFFF',
                      cursor: contactSubmitting ? 'not-allowed' : 'pointer',
                      opacity: contactSubmitting ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!contactSubmitting) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!contactSubmitting) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }
                    }}
                  >
                    {contactSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* RGPD Tab - Discreet page */}
      {activeTab === 'rgpd' && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-lg shadow-lg p-6" style={{backgroundColor: '#2C3148'}}>
            <h2 className="text-lg font-medium mb-6" style={{color: '#FFFFFF'}}>
              🔒 Protection des Données Personnelles (RGPD)
            </h2>
            
            {/* Consent Status */}
            <div className="mb-6 p-4 rounded-lg" style={{
              backgroundColor: profile?.rgpd_consent ? 'rgba(0, 255, 179, 0.1)' : 'rgba(255, 87, 87, 0.1)',
              border: `1px solid ${profile?.rgpd_consent ? '#00FFB3' : '#FF5757'}`
            }}>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{profile?.rgpd_consent ? '✅' : '⚠️'}</span>
                  <div>
                    <p className="font-medium" style={{color: profile?.rgpd_consent ? '#00FFB3' : '#FF5757'}}>
                      {profile?.rgpd_consent ? 'Consentement actif' : 'Consentement requis'}
                    </p>
                    {profile?.rgpd_consent && profile?.rgpd_consent_date && (
                      <p className="text-xs" style={{color: '#8A92B2'}}>
                        Accepté le {new Date(profile.rgpd_consent_date).toLocaleDateString('fr-FR')} (version {profile.rgpd_consent_version || '1.0'})
                      </p>
                    )}
                    {!profile?.rgpd_consent && (
                      <p className="text-xs" style={{color: '#FF8A8A'}}>
                        L&apos;accès aux fonctionnalités est bloqué
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRGPDConsent(!profile?.rgpd_consent)}
                  disabled={consentLoading}
                  className="px-4 py-2 rounded-md text-sm font-medium transition-all duration-300"
                  style={{
                    backgroundColor: profile?.rgpd_consent ? 'transparent' : '#00FFB3',
                    color: profile?.rgpd_consent ? '#FF5757' : '#1A1E2E',
                    border: profile?.rgpd_consent ? '1px solid #FF5757' : 'none',
                    opacity: consentLoading ? 0.7 : 1
                  }}
                >
                  {consentLoading ? '⏳...' : profile?.rgpd_consent ? 'Retirer mon consentement' : 'Donner mon consentement'}
                </button>
              </div>
            </div>
            
            {/* Warning if no consent */}
            {!profile?.rgpd_consent && (
              <div className="mb-6 p-4 rounded-lg" style={{backgroundColor: 'rgba(255, 87, 87, 0.1)', border: '1px solid #FF5757'}}>
                <p style={{color: '#FF8A8A', fontSize: '14px'}}>
                  <strong>⚠️ Attention :</strong> Sans consentement, l&apos;accès aux fonctionnalités suivantes est bloqué :
                </p>
                <ul style={{color: '#B8C0E0', fontSize: '13px', paddingLeft: '20px', marginTop: '8px'}}>
                  <li>Gestion du cimetière</li>
                  <li>Gestion du plan</li>
                  <li>Ajout/modification de concessions et personnes</li>
                </ul>
              </div>
            )}
            
            {/* Actions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                className="p-4 rounded-lg text-left transition-all duration-300 hover:border-opacity-100"
                style={{
                  backgroundColor: '#1C2133',
                  border: '1px solid #3A4359'
                }}
              >
                <span className="text-xl block mb-2">📋</span>
                <p className="font-medium text-sm" style={{color: '#FFFFFF'}}>Politique de confidentialité</p>
                <p className="text-xs" style={{color: '#8A92B2'}}>Consulter les détails du traitement</p>
              </button>
              
              <button
                onClick={handleRGPDExport}
                disabled={rgpdExportLoading}
                className="p-4 rounded-lg text-left transition-all duration-300"
                style={{
                  backgroundColor: '#1C2133',
                  border: '1px solid #3A4359',
                  opacity: rgpdExportLoading ? 0.7 : 1
                }}
              >
                <span className="text-xl block mb-2">{rgpdExportLoading ? '⏳' : '📥'}</span>
                <p className="font-medium text-sm" style={{color: '#FFFFFF'}}>
                  {rgpdExportLoading ? 'Export en cours...' : 'Exporter mes données'}
                </p>
                <p className="text-xs" style={{color: '#8A92B2'}}>Télécharger au format JSON</p>
              </button>
              
              <button
                onClick={() => setShowRGPDModal(true)}
                className="p-4 rounded-lg text-left transition-all duration-300"
                style={{
                  backgroundColor: '#1C2133',
                  border: '1px solid #3A4359'
                }}
              >
                <span className="text-xl block mb-2">👁️</span>
                <p className="font-medium text-sm" style={{color: '#FFFFFF'}}>Voir mes données</p>
                <p className="text-xs" style={{color: '#8A92B2'}}>Droit d&apos;accès</p>
              </button>
              
              <button
                onClick={() => setShowDeleteAccountModal(true)}
                className="p-4 rounded-lg text-left transition-all duration-300"
                style={{
                  backgroundColor: '#1C2133',
                  border: '1px solid #FF5757'
                }}
              >
                <span className="text-xl block mb-2">🗑️</span>
                <p className="font-medium text-sm" style={{color: '#FF5757'}}>Supprimer mon compte</p>
                <p className="text-xs" style={{color: '#8A92B2'}}>Droit à l&apos;oubli</p>
              </button>
            </div>
            
            {/* Data Summary - Compact */}
            <div className="p-4 rounded-lg" style={{backgroundColor: '#1C2133'}}>
              <h3 className="text-sm font-medium mb-3" style={{color: '#8A92B2'}}>Résumé de vos données</h3>
              <div className="flex flex-wrap gap-6 text-center">
                <div>
                  <p className="text-lg font-bold" style={{color: '#FFFFFF'}}>{concessions.length}</p>
                  <p className="text-xs" style={{color: '#5B638C'}}>Concessions</p>
                </div>
                <div>
                  <p className="text-lg font-bold" style={{color: '#FFFFFF'}}>
                    {concessions.reduce((acc, c) => acc + (c.persons?.length || 0), 0)}
                  </p>
                  <p className="text-xs" style={{color: '#5B638C'}}>Personnes</p>
                </div>
                <div>
                  <p className="text-lg font-bold" style={{color: '#FFFFFF'}}>
                    {concessions.reduce((acc, c) => acc + (c.files?.length || 0), 0)}
                  </p>
                  <p className="text-xs" style={{color: '#5B638C'}}>Documents</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={() => {
          setMessageModal({
            isOpen: true,
            title: 'Succès',
            message: 'Mot de passe modifié avec succès!',
            type: 'success'
          });
        }}
      />

      {/* Concession Modal - Enhanced Form with Labels */}
      {showConcessionModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)'}}
          onClick={handleCloseConcessionModal}>
          <div style={{backgroundColor: '#2C3148', padding: '32px', borderRadius: '16px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #3A4359'}}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{color: '#00C2FF', marginBottom: '24px', fontSize: '24px', fontWeight: '700', borderBottom: '2px solid #4A5BFF', paddingBottom: '12px'}}>
              {editingConcession ? '✏️ Modifier la concession' : '➕ Nouvelle concession'}
            </h3>
            <form id="concession-form" onSubmit={(e) => {
              e.preventDefault();
              handleSaveConcession(concessionFormData);
            }}>
              <div style={{display: 'grid', gap: '18px'}}>
                {/* Numéro de plan */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Numéro de plan <span style={{color: '#FF5757'}}>*</span>
                  </label>
                  <input name="num_plan" required value={concessionFormData.num_plan}
                    onChange={(e) => handleConcessionFormChange('num_plan', e.target.value)}
                    placeholder="Ex: A-123, B-45..."
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Acheteur */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Acheteur
                  </label>
                  <input name="acheteur" value={concessionFormData.acheteur}
                    onChange={(e) => handleConcessionFormChange('acheteur', e.target.value)}
                    placeholder="Nom de l'acheteur de la concession"
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Numéro de concession */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Numéro de concession
                  </label>
                  <input name="num_concession" value={concessionFormData.num_concession}
                    onChange={(e) => handleConcessionFormChange('num_concession', e.target.value)}
                    placeholder="Ex: CONC-001, 123..."
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Date d&apos;achat */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Date d&apos;achat
                  </label>
                  <input type="date" name="date_achat" value={concessionFormData.date_achat}
                    onChange={(e) => handleConcessionFormChange('date_achat', e.target.value)}
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Durée */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Durée de la concession
                  </label>
                  <input name="duree_concession" value={concessionFormData.duree_concession}
                    onChange={(e) => handleConcessionFormChange('duree_concession', e.target.value)}
                    placeholder="Ex: 15, 30, 50 ou 'perpétuelle'"
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                  <small style={{color: '#5B638C', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Nombre d&apos;années ou &quot;perpétuelle&quot;
                  </small>
                </div>

                {/* Nombre de places */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Nombre de places
                  </label>
                  <input type="number" name="nombre_places" min="1" value={concessionFormData.nombre_places}
                    onChange={(e) => handleConcessionFormChange('nombre_places', parseInt(e.target.value) || 1)}
                    placeholder="Ex: 1, 2, 3..."
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                  <small style={{color: '#5B638C', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Nombre maximum de personnes dans cette concession
                  </small>
                </div>

                {/* Notes - Larger */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Notes
                  </label>
                  <textarea name="notes" rows="8" value={concessionFormData.notes}
                    onChange={(e) => handleConcessionFormChange('notes', e.target.value)}
                    placeholder="Notes libres sur cette concession..."
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px', resize: 'vertical', minHeight: '150px'}} />
                </div>

                {/* File Upload Section - Show for both create and edit (hidden for demo) */}
                {!isDemoMode && (
                <div style={{marginTop: '12px', padding: '20px', backgroundColor: '#1A1E2E', borderRadius: '12px', border: '1px solid #3A4359'}}>
                  <h4 style={{color: '#00C2FF', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    📎 Fichiers joints ({editingConcession ? concessionFiles.length : tempConcessionFiles.length}/4)
                    </h4>
                  
                  {/* Upload Button */}
                  {((editingConcession && concessionFiles.length < 4) || (!editingConcession && tempConcessionFiles.length < 4)) && (
                    <label style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      backgroundColor: 'rgba(0, 194, 255, 0.15)',
                      color: '#00C2FF',
                      border: '1px solid rgba(0, 194, 255, 0.3)',
                      borderRadius: '8px',
                      cursor: uploadingFile ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      marginBottom: '16px',
                      opacity: uploadingFile ? 0.6 : 1
                    }}>
                      {uploadingFile ? '⏳ Téléchargement...' : '📤 Ajouter un fichier'}
                      <input 
                        type="file" 
                        accept=".txt,.pdf,.jpg,.jpeg,.png,.gif"
                        disabled={uploadingFile}
                        style={{display: 'none'}}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            if (editingConcession) {
                              handleFileUpload(editingConcession.id, e.target.files[0]);
                            } else {
                              handleTempConcessionFileAdd(e.target.files[0]);
                            }
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  )}
                    
                  <small style={{display: 'block', color: '#5B638C', fontSize: '12px', marginTop: '8px', marginBottom: '12px'}}>
                    Formats acceptés : .txt, .pdf, .jpg, .png, .gif (max 10MB)
                  </small>

                  {/* Files List - Using DocumentCard component */}
                  {(() => {
                    const displayFiles = editingConcession ? concessionFiles : tempConcessionFiles;
                    const isTemp = !editingConcession;
                    
                    if (displayFiles.length === 0) {
                      return (
                        <div style={{
                          textAlign: 'center', 
                          padding: '30px',
                          backgroundColor: 'rgba(138, 146, 178, 0.05)',
                          borderRadius: '8px',
                          border: '1px dashed rgba(138, 146, 178, 0.2)'
                        }}>
                          <span style={{fontSize: '32px', opacity: 0.5, display: 'block', marginBottom: '8px'}}>📁</span>
                          <p style={{color: '#5B638C', fontSize: '14px', margin: 0}}>
                            Aucun fichier joint
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px'}}>
                        {displayFiles.map(file => (
                          <DocumentCard
                            key={file.id}
                            file={file}
                            concessionId={editingConcession?.id}
                            personId={null}
                            isTemp={isTemp}
                            onView={(f) => {
                              if (editingConcession) {
                                handleFileView(f, editingConcession.id);
                              }
                            }}
                            onDownload={(f) => {
                              if (editingConcession) {
                                handleFileDownload(f, editingConcession.id);
                              }
                            }}
                            onDelete={(f, e) => {
                              if (editingConcession) {
                                handleFileDelete(editingConcession.id, f.id, e);
                              } else {
                                handleTempConcessionFileRemove(f.id, e);
                              }
                            }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                )}
              </div>

              {/* Demo mode warning in concession form */}
              {isDemoMode && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 184, 0, 0.1)',
                  border: '1px solid rgba(255, 184, 0, 0.3)',
                  borderRadius: '8px'
                }}>
                  <p style={{color: '#FFB800', fontSize: '13px', margin: 0}}>
                    🔒 Mode démonstration : l&apos;enregistrement et les fichiers joints sont désactivés.
                  </p>
                </div>
              )}

              <div style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
                <button type="button" onClick={handleCloseConcessionModal}
                  style={{flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#8A92B2', border: '2px solid #3A4359', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', transition: 'all 0.2s'}}
                  onMouseEnter={(e) => {e.currentTarget.style.borderColor = '#FF5757'; e.currentTarget.style.color = '#FF5757';}}
                  onMouseLeave={(e) => {e.currentTarget.style.borderColor = '#3A4359'; e.currentTarget.style.color = '#8A92B2';}}>
                  Annuler
                </button>
                <button type="submit" disabled={isDemoMode}
                  style={{flex: 1, padding: '14px', background: isDemoMode ? 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)' : 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)', color: isDemoMode ? '#8A8A8A' : '#FFF', border: 'none', borderRadius: '8px', cursor: isDemoMode ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '700', transition: 'all 0.2s', boxShadow: isDemoMode ? 'none' : '0 4px 12px rgba(74, 91, 255, 0.3)', opacity: isDemoMode ? 0.6 : 1}}
                  onMouseEnter={(e) => { if (!isDemoMode) {e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(74, 91, 255, 0.5)';}}}
                  onMouseLeave={(e) => {e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDemoMode ? 'none' : '0 4px 12px rgba(74, 91, 255, 0.3)';}}>
                  {isDemoMode ? '🔒 Enregistrer' : '💾 Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Person Modal - Enhanced Form with Labels and Notes */}
      {showPersonModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)'}}
          onClick={handleClosePersonModal}>
          <div style={{backgroundColor: '#2C3148', padding: '32px', borderRadius: '16px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #3A4359'}}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{color: '#7BA591', marginBottom: '24px', fontSize: '24px', fontWeight: '700', borderBottom: '2px solid #7BA591', paddingBottom: '12px'}}>
              {editingPerson ? '✏️ Modifier la personne' : '➕ Nouvelle personne'}
            </h3>
            <form id="person-form" onSubmit={(e) => {
              e.preventDefault();
              handleSavePerson(personFormData);
            }}>
              <div style={{display: 'grid', gap: '18px'}}>
                {/* Nom */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Nom <span style={{color: '#FF5757'}}>*</span>
                  </label>
                  <input name="nom" required value={personFormData.nom}
                    onChange={(e) => handlePersonFormChange('nom', e.target.value)}
                    placeholder="Nom de famille"
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Prénom */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Prénom
                  </label>
                  <input name="prenom" value={personFormData.prenom}
                    onChange={(e) => handlePersonFormChange('prenom', e.target.value)}
                    placeholder="Prénom"
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                </div>

                {/* Date de naissance et Date de décès */}
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px'}}>
                  <div>
                    <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                      Date de naissance
                    </label>
                    <input name="date_naissance" type="date" value={personFormData.date_naissance}
                      onChange={(e) => handlePersonFormChange('date_naissance', e.target.value)}
                      style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                  </div>
                  <div>
                    <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                      Date de décès
                    </label>
                    <input name="date_deces" type="date" value={personFormData.date_deces}
                      onChange={(e) => handlePersonFormChange('date_deces', e.target.value)}
                      style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px'}} />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label style={{display: 'block', color: '#8A92B2', fontSize: '14px', fontWeight: '600', marginBottom: '6px'}}>
                    Notes
                  </label>
                  <textarea name="notes" rows="8" value={personFormData.notes}
                    onChange={(e) => handlePersonFormChange('notes', e.target.value)}
                    placeholder="Notes libres sur cette personne..."
                    style={{width: '100%', padding: '12px', backgroundColor: '#1C2133', border: '2px solid #3A4359', borderRadius: '8px', color: '#FFF', fontSize: '14px', resize: 'vertical', minHeight: '150px'}} />
                </div>

                {/* File Upload Section for Person (hidden for demo) */}
                {!isDemoMode && (
                <div style={{marginTop: '12px', padding: '20px', backgroundColor: '#1A1E2E', borderRadius: '12px', border: '1px solid #3A4359'}}>
                  <h4 style={{color: '#00FFB3', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    📎 Documents ({editingPerson ? personFiles.length : tempPersonFiles.length}/4)
                  </h4>
                  
                  {/* Upload Button */}
                  {((editingPerson && personFiles.length < 4) || (!editingPerson && tempPersonFiles.length < 4)) && (
                    <label style={{
                      display: 'inline-block',
                      padding: '10px 16px',
                      backgroundColor: 'rgba(0, 255, 179, 0.15)',
                      color: '#00FFB3',
                      border: '1px solid rgba(0, 255, 179, 0.4)',
                      borderRadius: '8px',
                      cursor: uploadingFile ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      marginBottom: '16px',
                      opacity: uploadingFile ? 0.6 : 1
                    }}>
                      {uploadingFile ? '⏳ Téléchargement...' : '📤 Ajouter un fichier'}
                      <input 
                        type="file" 
                        accept=".txt,.pdf,.jpg,.jpeg,.png,.gif"
                        disabled={uploadingFile}
                        style={{display: 'none'}}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            if (editingPerson && currentConcessionForPerson) {
                              handlePersonFileUpload(currentConcessionForPerson.id, editingPerson.id, e.target.files[0]);
                            } else {
                              handleTempPersonFileAdd(e.target.files[0]);
                            }
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  )}
                  
                  <small style={{display: 'block', color: '#5B638C', fontSize: '12px', marginTop: '8px', marginBottom: '12px'}}>
                    Formats acceptés : .txt, .pdf, .jpg, .png, .gif (max 10MB)
                  </small>

                  {/* Files List - Using DocumentCard component */}
                  {(() => {
                    const displayFiles = editingPerson ? personFiles : tempPersonFiles;
                    const isTemp = !editingPerson;
                    
                    if (displayFiles.length === 0) {
                      return (
                        <div style={{
                          textAlign: 'center', 
                          padding: '30px',
                          backgroundColor: 'rgba(138, 146, 178, 0.05)',
                          borderRadius: '8px',
                          border: '1px dashed rgba(138, 146, 178, 0.2)'
                        }}>
                          <span style={{fontSize: '32px', opacity: 0.5, display: 'block', marginBottom: '8px'}}>📁</span>
                          <p style={{color: '#5B638C', fontSize: '14px', margin: 0}}>
                            Aucun document joint
                          </p>
                        </div>
                      );
                    }
                    
                    return (
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px'}}>
                        {displayFiles.map(file => (
                          <DocumentCard
                            key={file.id}
                            file={file}
                            concessionId={currentConcessionForPerson?.id}
                            personId={editingPerson?.id}
                            isTemp={isTemp}
                            onView={(f) => {
                              if (editingPerson && currentConcessionForPerson) {
                                handleFileView(f, currentConcessionForPerson.id, editingPerson.id);
                              }
                            }}
                            onDownload={(f) => {
                              if (editingPerson && currentConcessionForPerson) {
                                handleFileDownload(f, currentConcessionForPerson.id, editingPerson.id);
                              }
                            }}
                            onDelete={(f, e) => {
                              if (editingPerson && currentConcessionForPerson) {
                                handlePersonFileDelete(currentConcessionForPerson.id, editingPerson.id, f.id, e);
                              } else {
                                handleTempPersonFileRemove(f.id, e);
                              }
                            }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                </div>
                )}
              </div>

              {/* Demo mode warning in person form */}
              {isDemoMode && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: 'rgba(255, 184, 0, 0.1)',
                  border: '1px solid rgba(255, 184, 0, 0.3)',
                  borderRadius: '8px'
                }}>
                  <p style={{color: '#FFB800', fontSize: '13px', margin: 0}}>
                    🔒 Mode démonstration : l&apos;enregistrement et les fichiers joints sont désactivés.
                  </p>
                </div>
              )}

              <div style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
                <button type="button" onClick={handleClosePersonModal}
                  style={{flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#8A92B2', border: '2px solid #3A4359', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', transition: 'all 0.2s'}}
                  onMouseEnter={(e) => {e.currentTarget.style.borderColor = '#FF5757'; e.currentTarget.style.color = '#FF5757';}}
                  onMouseLeave={(e) => {e.currentTarget.style.borderColor = '#3A4359'; e.currentTarget.style.color = '#8A92B2';}}>
                  Annuler
                </button>
                <button type="submit" disabled={isDemoMode}
                  style={{flex: 1, padding: '14px', background: isDemoMode ? 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)' : 'linear-gradient(135deg, #00C2FF 0%, #00FFB3 100%)', color: isDemoMode ? '#8A8A8A' : '#1A1E2E', border: 'none', borderRadius: '8px', cursor: isDemoMode ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '700', transition: 'all 0.2s', boxShadow: isDemoMode ? 'none' : '0 4px 12px rgba(0, 194, 255, 0.3)', opacity: isDemoMode ? 0.6 : 1}}
                  onMouseEnter={(e) => { if (!isDemoMode) {e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 194, 255, 0.5)';}}}
                  onMouseLeave={(e) => {e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDemoMode ? 'none' : '0 4px 12px rgba(0, 194, 255, 0.3)';}}>
                  {isDemoMode ? '🔒 Enregistrer' : '💾 Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      {unsavedChangesModal.isOpen && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(6px)'}}>
          <div style={{backgroundColor: '#2C3148', padding: '32px', borderRadius: '16px', maxWidth: '450px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', border: '1px solid #FFB800'}}>
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <span style={{fontSize: '48px', display: 'block', marginBottom: '16px'}}>⚠️</span>
              <h3 style={{color: '#FFB800', fontSize: '20px', fontWeight: '700', marginBottom: '12px'}}>
                Modifications non sauvegardées
              </h3>
              <p style={{color: '#8A92B2', fontSize: '14px', lineHeight: '1.6'}}>
                Vous avez des modifications en cours qui n&apos;ont pas été enregistrées. Que souhaitez-vous faire ?
              </p>
            </div>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              <button 
                onClick={isDemoMode ? unsavedChangesModal.onDiscard : unsavedChangesModal.onSave}
                disabled={isDemoMode}
                style={{
                  width: '100%', 
                  padding: '14px', 
                  background: isDemoMode 
                    ? 'linear-gradient(135deg, #4A4A4A 0%, #3A3A3A 100%)'
                    : 'linear-gradient(135deg, #4A5BFF 0%, #7A5FFF 100%)', 
                  color: isDemoMode ? '#8A8A8A' : '#FFF', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: isDemoMode ? 'not-allowed' : 'pointer', 
                  fontSize: '16px', 
                  fontWeight: '700',
                  transition: 'all 0.2s',
                  boxShadow: isDemoMode ? 'none' : '0 4px 12px rgba(74, 91, 255, 0.3)',
                  opacity: isDemoMode ? 0.6 : 1
                }}
                title={isDemoMode ? 'Fonctionnalité désactivée en mode démo' : ''}
              >
                {isDemoMode ? '🔒 Sauvegarder et fermer' : '💾 Sauvegarder et fermer'}
              </button>
              <button 
                onClick={unsavedChangesModal.onDiscard}
                style={{
                  width: '100%', 
                  padding: '14px', 
                  backgroundColor: 'transparent', 
                  color: '#FF5757', 
                  border: '2px solid #FF5757', 
                  borderRadius: '8px', 
                  cursor: 'pointer', 
                  fontSize: '16px', 
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                🗑️ Ne pas sauvegarder
              </button>
              <button 
                onClick={() => setUnsavedChangesModal({ isOpen: false, type: '', onSave: () => {}, onDiscard: () => {} })}
                style={{
                  width: '100%', 
                  padding: '14px', 
                  backgroundColor: 'transparent', 
                  color: '#8A92B2', 
                  border: '2px solid #3A4359', 
                  borderRadius: '8px', 
                  cursor: 'pointer', 
                  fontSize: '16px', 
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                ← Continuer l&apos;édition
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Document Viewer Modal */}
      {lightboxFile && (
        <DocumentViewerModal
          file={lightboxFile}
          concessionId={lightboxFile.concessionId}
          personId={lightboxFile.personId}
          onClose={() => setLightboxFile(null)}
          onDownload={(f) => handleFileDownload(f, lightboxFile.concessionId, lightboxFile.personId)}
          onDelete={(f, e) => {
            if (lightboxFile.personId) {
              handlePersonFileDelete(lightboxFile.concessionId, lightboxFile.personId, f.id, e);
            } else {
              handleFileDelete(lightboxFile.concessionId, f.id, e);
            }
            setLightboxFile(null);
          }}
        />
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
      />
      
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={() => setMessageModal({ ...messageModal, isOpen: false })}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
      />
      
      {/* PDF Success Modal */}
      {showPdfSuccessModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowPdfSuccessModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #00C2FF',
              borderRadius: '12px',
              padding: '30px 40px',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0, 194, 255, 0.3)',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
              <h3 style={{ 
                color: '#00C2FF', 
                fontSize: '24px', 
                fontWeight: 'bold',
                marginBottom: '15px'
              }}>
                PDF généré avec succès !
              </h3>
              <div style={{ 
                color: '#8A92B2', 
                fontSize: '16px',
                marginBottom: '10px' 
              }}>
                <span style={{ color: '#00FFB3', fontWeight: 'bold' }}>{pdfModalInfo.persons}</span> personne(s) imprimée(s)
              </div>
              <div style={{ 
                color: '#8A92B2', 
                fontSize: '16px',
                marginBottom: '25px' 
              }}>
                <span style={{ color: '#00C2FF', fontWeight: 'bold' }}>{pdfModalInfo.pages}</span> page(s) générée(s)
              </div>
              <button
                onClick={() => setShowPdfSuccessModal(false)}
                style={{
                  background: 'linear-gradient(135deg, #00C2FF 0%, #0099CC 100%)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 194, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* RGPD Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowPrivacyPolicy(false)}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #00C2FF',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 194, 255, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#00C2FF', fontSize: '22px', fontWeight: 'bold', margin: 0 }}>
                🔒 Politique de Confidentialité
              </h2>
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                style={{ background: 'none', border: 'none', color: '#8A92B2', fontSize: '24px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ color: '#FFFFFF', lineHeight: '1.6' }}>
              <p style={{ color: '#8A92B2', fontSize: '12px', marginBottom: '20px' }}>
                Version 1.0 - Dernière mise à jour : Janvier 2026
              </p>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>📊 Données collectées</h3>
              <ul style={{ color: '#B8C0E0', paddingLeft: '20px', marginBottom: '15px' }}>
                <li><strong>Identification :</strong> Adresse email, nom du maire, nom du secrétaire</li>
                <li><strong>Contact :</strong> Numéro de téléphone, adresse de la mairie</li>
                <li><strong>Gestion du cimetière :</strong> Concessions, personnes inhumées, documents</li>
                <li><strong>Technique :</strong> Dates de création et modification du compte</li>
              </ul>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>🎯 Finalités du traitement</h3>
              <ul style={{ color: '#B8C0E0', paddingLeft: '20px', marginBottom: '15px' }}>
                <li>Gestion de votre compte utilisateur</li>
                <li>Gestion des concessions funéraires de votre commune</li>
                <li>Communication et support technique</li>
                <li>Sécurité et audit des accès</li>
              </ul>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>⏱️ Durée de conservation</h3>
              <p style={{ color: '#B8C0E0', marginBottom: '15px' }}>
                Vos données sont conservées pendant la durée de votre abonnement actif, puis 3 ans après expiration pour archivage légal. Vous pouvez demander la suppression à tout moment.
              </p>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>⚖️ Vos droits</h3>
              <ul style={{ color: '#B8C0E0', paddingLeft: '20px', marginBottom: '15px' }}>
                <li><strong>Droit d&apos;accès :</strong> Consulter vos données depuis votre profil</li>
                <li><strong>Droit de rectification :</strong> Modifier vos informations à tout moment</li>
                <li><strong>Droit à l&apos;effacement :</strong> Supprimer votre compte et toutes vos données</li>
                <li><strong>Droit à la portabilité :</strong> Exporter vos données au format JSON</li>
              </ul>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>🔐 Sécurité</h3>
              <ul style={{ color: '#B8C0E0', paddingLeft: '20px', marginBottom: '15px' }}>
                <li>Mots de passe chiffrés (bcrypt)</li>
                <li>Authentification par token JWT</li>
                <li>Contrôle d&apos;accès basé sur les rôles</li>
                <li>Fichiers accessibles uniquement via authentification</li>
              </ul>
              
              <h3 style={{ color: '#00FFB3', marginTop: '20px', marginBottom: '10px' }}>📧 Contact</h3>
              <p style={{ color: '#B8C0E0' }}>
                Pour toute question relative à vos données personnelles : <span style={{color: '#00C2FF'}}>contact@cimeo.fr</span>
              </p>
            </div>
            
            <button
              onClick={() => setShowPrivacyPolicy(false)}
              style={{
                marginTop: '25px',
                width: '100%',
                background: 'linear-gradient(135deg, #00C2FF 0%, #0099CC 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              J&apos;ai compris
            </button>
          </div>
        </div>
      )}
      
      {/* RGPD Data View Modal */}
      {showRGPDModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowRGPDModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #00C2FF',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 194, 255, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#00C2FF', fontSize: '22px', fontWeight: 'bold', margin: 0 }}>
                👁️ Mes Données Personnelles
              </h2>
              <button
                onClick={() => setShowRGPDModal(false)}
                style={{ background: 'none', border: 'none', color: '#8A92B2', fontSize: '24px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            
            {profile && (
              <div style={{ color: '#FFFFFF' }}>
                <div style={{ backgroundColor: '#242A3D', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <h4 style={{ color: '#00FFB3', marginBottom: '10px', fontSize: '14px' }}>📧 Identification</h4>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Email :</strong> {profile.email}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Rôle :</strong> {profile.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Compte créé le :</strong> {new Date(profile.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                
                <div style={{ backgroundColor: '#242A3D', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <h4 style={{ color: '#00FFB3', marginBottom: '10px', fontSize: '14px' }}>🏛️ Informations Mairie</h4>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Adresse :</strong> {profile.city_hall_address || 'Non renseigné'}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Maire :</strong> {profile.mayor_name || 'Non renseigné'}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Secrétaire :</strong> {profile.secretary_name || 'Non renseigné'}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Téléphone :</strong> {profile.phone_number || 'Non renseigné'}</p>
                </div>
                
                <div style={{ backgroundColor: '#242A3D', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                  <h4 style={{ color: '#00FFB3', marginBottom: '10px', fontSize: '14px' }}>⚰️ Données Cimetière</h4>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Concessions :</strong> {concessions.length}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Personnes enregistrées :</strong> {concessions.reduce((acc, c) => acc + (c.persons?.length || 0), 0)}</p>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Documents joints :</strong> {concessions.reduce((acc, c) => acc + (c.files?.length || 0), 0)}</p>
                </div>
                
                <div style={{ backgroundColor: '#242A3D', borderRadius: '8px', padding: '15px' }}>
                  <h4 style={{ color: '#00FFB3', marginBottom: '10px', fontSize: '14px' }}>✅ Consentement RGPD</h4>
                  <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Statut :</strong> {profile.rgpd_consent ? 'Consentement donné' : 'Non consenti'}</p>
                  {profile.rgpd_consent_date && (
                    <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Date :</strong> {new Date(profile.rgpd_consent_date).toLocaleDateString('fr-FR')}</p>
                  )}
                  {profile.rgpd_consent_version && (
                    <p style={{ color: '#B8C0E0', fontSize: '13px' }}><strong>Version :</strong> {profile.rgpd_consent_version}</p>
                  )}
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleRGPDExport}
                disabled={rgpdExportLoading}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #00FFB3 0%, #00C2FF 100%)',
                  color: '#1A1E2E',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {rgpdExportLoading ? '⏳ Export...' : '📥 Exporter (JSON)'}
              </button>
              <button
                onClick={() => setShowRGPDModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#2C3148',
                  color: '#8A92B2',
                  border: '1px solid #4A5BFF',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* RGPD Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => !deleteAccountLoading && setShowDeleteAccountModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #FF5757',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(255, 87, 87, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
              <h2 style={{ color: '#FF5757', fontSize: '22px', fontWeight: 'bold', marginBottom: '15px' }}>
                Supprimer mon compte
              </h2>
              <p style={{ color: '#B8C0E0', marginBottom: '10px', lineHeight: '1.6' }}>
                Cette action est <strong style={{color: '#FF5757'}}>irréversible</strong>. Toutes vos données seront définitivement supprimées :
              </p>
              <ul style={{ color: '#8A92B2', textAlign: 'left', paddingLeft: '30px', marginBottom: '20px', fontSize: '14px' }}>
                <li>Votre compte utilisateur</li>
                <li>Toutes vos concessions ({concessions.length})</li>
                <li>Toutes les personnes enregistrées ({concessions.reduce((acc, c) => acc + (c.persons?.length || 0), 0)})</li>
                <li>Tous les documents joints ({concessions.reduce((acc, c) => acc + (c.files?.length || 0), 0)})</li>
              </ul>
              
              <div style={{ 
                backgroundColor: 'rgba(255, 87, 87, 0.1)', 
                border: '1px solid #FF5757', 
                borderRadius: '8px', 
                padding: '15px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#FF5757', fontSize: '14px', fontWeight: 'bold' }}>
                  Êtes-vous absolument certain de vouloir continuer ?
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowDeleteAccountModal(false)}
                  disabled={deleteAccountLoading}
                  style={{
                    flex: 1,
                    backgroundColor: '#2C3148',
                    color: '#FFFFFF',
                    border: '1px solid #4A5BFF',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteOwnAccount}
                  disabled={deleteAccountLoading}
                  style={{
                    flex: 1,
                    backgroundColor: '#FF5757',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    opacity: deleteAccountLoading ? 0.7 : 1
                  }}
                >
                  {deleteAccountLoading ? '⏳ Suppression...' : '🗑️ Confirmer la suppression'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* RGPD First Login Consent Modal - Blocking */}
      {showFirstLoginConsentModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000
          }}
        >
          <div 
            style={{
              backgroundColor: '#1C2133',
              border: '2px solid #00C2FF',
              borderRadius: '16px',
              padding: '40px',
              maxWidth: '600px',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 30px 80px rgba(0, 194, 255, 0.4)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ fontSize: '60px', marginBottom: '15px' }}>🔒</div>
              <h2 style={{ color: '#00C2FF', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                Protection de vos données
              </h2>
              <p style={{ color: '#8A92B2', fontSize: '14px', marginTop: '10px' }}>
                Conformité RGPD - Règlement Général sur la Protection des Données
              </p>
            </div>
            
            <div style={{ 
              backgroundColor: '#242A3D', 
              borderRadius: '12px', 
              padding: '20px', 
              marginBottom: '25px',
              border: '1px solid #3A4359'
            }}>
              <p style={{ color: '#B8C0E0', lineHeight: '1.7', fontSize: '14px', marginBottom: '15px' }}>
                Pour utiliser <strong style={{color: '#00C2FF'}}>CIMÉO</strong>, nous avons besoin de votre consentement explicite pour le traitement de vos données personnelles.
              </p>
              
              <h4 style={{ color: '#00FFB3', fontSize: '14px', marginBottom: '10px' }}>📊 Données collectées :</h4>
              <ul style={{ color: '#8A92B2', fontSize: '13px', paddingLeft: '20px', marginBottom: '15px' }}>
                <li>Informations de compte (email, mot de passe chiffré)</li>
                <li>Informations de la mairie (adresse, nom du maire, secrétaire)</li>
                <li>Données de gestion du cimetière (concessions, personnes, documents)</li>
              </ul>
              
              <h4 style={{ color: '#00FFB3', fontSize: '14px', marginBottom: '10px' }}>🎯 Finalités :</h4>
              <ul style={{ color: '#8A92B2', fontSize: '13px', paddingLeft: '20px', marginBottom: '15px' }}>
                <li>Gestion de votre compte et de vos abonnements</li>
                <li>Gestion des concessions funéraires de votre commune</li>
                <li>Communication et support technique</li>
              </ul>
              
              <h4 style={{ color: '#00FFB3', fontSize: '14px', marginBottom: '10px' }}>⚖️ Vos droits :</h4>
              <p style={{ color: '#8A92B2', fontSize: '13px' }}>
                Vous disposez d&apos;un droit d&apos;accès, de rectification, d&apos;effacement et de portabilité de vos données. 
                Vous pouvez retirer votre consentement à tout moment depuis l&apos;onglet &quot;RGPD&quot;.
              </p>
            </div>
            
            <div style={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid #FFC107',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '25px'
            }}>
              <p style={{ color: '#FFC107', fontSize: '13px', margin: 0 }}>
                <strong>⚠️ Important :</strong> Sans votre consentement, vous ne pourrez pas accéder aux fonctionnalités de gestion du cimetière et du plan.
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleFirstLoginConsent}
                disabled={consentLoading}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #00FFB3 0%, #00C2FF 100%)',
                  color: '#1A1E2E',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  opacity: consentLoading ? 0.7 : 1
                }}
              >
                {consentLoading ? '⏳ Enregistrement...' : '✅ J\'accepte et je consens au traitement de mes données'}
              </button>
              
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  color: '#8A92B2',
                  border: '1px solid #3A4359',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                📋 Lire la politique de confidentialité complète
              </button>
              
              <button
                onClick={() => {
                  setShowFirstLoginConsentModal(false);
                  // User can continue but with limited access
                }}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  color: '#5B638C',
                  border: 'none',
                  padding: '10px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Continuer sans accepter (accès limité)
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Hidden print content */}
      <div id="print-content"></div>
    </div>
  );
};


export default ClientDashboard;
